/**
 * Marcel — le barman du Bar à Chaussettes — Widget FAQ embarqué
 * Web Component vanilla ES2022, shadow DOM ouvert
 * Fichier unique, < 50 kB non gzippé / < 12 kB gzippé
 * Pas de dépendance externe
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Génère un UUID v4 simple (crypto.randomUUID si dispo, sinon fallback Math) */
function generateUUID() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Nombre maximum de cartes produit affichées dans une réponse (le reste via « voir tous »). */
const MAX_PRODUCT_CARDS = 5;

/** Formate un montant Shopify (centimes entier) en chaîne « 12,00 € ». */
function formatMoneyCents(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  return (n / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/** Échappe les caractères HTML pour éviter les injections XSS */
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Rendu maison minimal markdown → HTML sécurisé
 * Supporte : [texte](url) → <a>, **bold** → <strong>, et URL nue https?:// → <a>
 * Tout le reste est échappé.
 */
function renderMarkdown(text) {
  if (!text) return '';

  // Whitelist d'URL : http, https, mailto, chemins relatifs /...
  function isSafeUrl(url) {
    return /^(https?:\/\/|mailto:|\/)/i.test(url.trim());
  }

  // Traitement caractère par caractère avec des passes regex successives
  // 1. On échappe d'abord tout le HTML brut
  let escaped = escapeHtml(text);

  // 2. On rétablit les patterns markdown sur le texte échappé
  // [texte](url) — on reconnaît les balises dans le texte échappé
  // Note : après escapeHtml, les & dans l'URL seront &amp; — on les restaure pour les URLs
  escaped = escaped.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_, linkText, rawUrl) => {
      // rawUrl peut contenir des entités HTML issues de l'escape (&amp; etc.)
      const url = rawUrl.replace(/&amp;/g, '&').replace(/&#39;/g, "'");
      if (isSafeUrl(url)) {
        const safeUrl = url.replace(/"/g, '&quot;');
        const target = url.startsWith('/') ? '' : ' target="_blank" rel="noopener noreferrer"';
        return `<a href="${safeUrl}"${target}>${linkText}</a>`;
      }
      // URL non sûre → texte brut
      return linkText;
    }
  );

  // 3. Auto-link des URL nues https?:// qui ne sont PAS déjà dans un <a href="...">
  //    Filet pour les réponses où une URL apparaît en texte brut (KB seed avec
  //    URL en dur dans le champ answer, ou réponse LLM résiduelle). Sans ça
  //    l'URL s'affiche en texte non cliquable (bug staging 2026-06-18).
  //    Stratégie : on découpe sur les balises <a>...</a> déjà produites en
  //    étape 2 et on n'auto-linkifie QUE les segments hors balise.
  const URL_RE = /\bhttps?:\/\/[^\s<>"']+[^\s<>"',.;:!?)]/g;
  const SPLIT_RE = /(<a [^>]*>.*?<\/a>)/g;
  escaped = escaped.split(SPLIT_RE).map(chunk => {
    if (chunk.startsWith('<a ')) return chunk; // déjà un lien rendu
    return chunk.replace(URL_RE, (rawUrl) => {
      // rawUrl est déjà escape-HTML safe (& → &amp;) puisqu'on travaille sur
      // la string post-escapeHtml. On restaure pour la décision isSafeUrl.
      const url = rawUrl.replace(/&amp;/g, '&').replace(/&#39;/g, "'");
      if (!isSafeUrl(url)) return rawUrl;
      const safeHref = rawUrl.replace(/"/g, '&quot;');
      return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${rawUrl}</a>`;
    });
  }).join('');

  // **bold**
  escaped = escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Assemblage ligne à ligne : les lignes commençant par une puce (•, - ou *)
  // forment une vraie liste <ul> avec retrait suspendu (le texte qui déborde
  // s'aligne sous le texte de la puce, pas au ras de gauche — cf. CSS
  // .marcel-md-list). Les autres lignes sont jointes par <br>.
  const BULLET_RE = /^\s*[•\-*]\s+(.*)$/;
  const lines = escaped.split('\n');
  let html = '';
  let buffer = [];
  let listItems = null;

  const flushBuffer = () => {
    if (buffer.length) { html += buffer.join('<br>'); buffer = []; }
  };
  const flushList = () => {
    if (listItems && listItems.length) {
      html += '<ul class="marcel-md-list">' +
        listItems.map((it) => `<li>${it}</li>`).join('') +
        '</ul>';
    }
    listItems = null;
  };

  for (const line of lines) {
    const m = line.match(BULLET_RE);
    if (m) {
      if (listItems === null) { flushBuffer(); listItems = []; }
      listItems.push(m[1]);
    } else {
      flushList();
      buffer.push(line);
    }
  }
  flushBuffer();
  flushList();

  return html;
}

// ---------------------------------------------------------------------------
// CSS (injecté dans le shadow root)
// ---------------------------------------------------------------------------

const WIDGET_CSS = `
  :host {
    all: initial;
    font-family: 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }

  /* Bulle au repos */
  .marcel-bubble {
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #F8FF66;
    /* Filet noir de marque (jaune + contours noirs). box-sizing border-box
       pour que la bulle reste à 56px pile, filet compris. */
    border: 1px solid #0a0a0a;
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(0,0,0,0.24);
    z-index: 9998;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    padding: 0;
    overflow: hidden;   /* clippe l'avatar au cercle (box-shadow reste externe) */
  }

  .marcel-bubble:hover,
  .marcel-bubble:focus-visible {
    transform: scale(1.06);
    box-shadow: 0 6px 20px rgba(0,0,0,0.32);
    /* Anneau noir : le jaune de marque serait invisible sur la bulle jaune. */
    outline: 2px solid #0a0a0a;
    outline-offset: 2px;
  }

  .marcel-bubble svg {
    width: 26px;
    height: 26px;
    color: #ffffff;
    flex-shrink: 0;
  }

  /* Avatar de Marcel : remplit la bulle, le fond #0a0a0a de la bulle transparaît
     dans les zones transparentes du PNG (autour de la tête). */
  .marcel-bubble-face {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    user-select: none;
    -webkit-user-drag: none;
  }

  /* Avatar dans l'en-tête du panneau — rond, à la place du logo « M ». */
  .marcel-header-face {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    object-fit: cover;
    background: #F8FF66;
    border: 1px solid #0a0a0a;
    box-sizing: border-box;
    flex-shrink: 0;
    display: block;
    user-select: none;
    -webkit-user-drag: none;
  }

  /* Desktop : la bulle reste visible quand le panneau est ouvert (elle apparaît
     sous la fenêtre de chat). Sur mobile, le panneau est en plein écran : on
     masque alors la bulle via .is-hidden, scopée dans le @media ci-dessous. */

  /* Invite contextuelle (teaser) — bandeau accolé à gauche de la bulle.
     Rendue masquée ; _showTeaser() ajoute .is-visible après 10 s ou 40 % de
     scroll. Desktop uniquement (cf. @media plus bas) : sur mobile un bandeau
     flottant au-dessus du sticky ATC serait vécu comme une popup. */
  .marcel-teaser {
    position: fixed;
    bottom: 30px;
    right: 88px;
    max-width: 240px;
    background: #ffffff;
    color: #0a0a0a;
    border: 1px solid #0a0a0a;
    border-radius: 14px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.14);
    padding: 10px 30px 10px 14px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    cursor: pointer;
    text-align: left;
    z-index: 9998;
    opacity: 0;
    transform: translateX(8px);
    transition: opacity 0.18s ease, transform 0.18s ease;
    pointer-events: none;
  }

  .marcel-teaser.is-visible {
    opacity: 1;
    transform: translateX(0);
    pointer-events: auto;
  }

  .marcel-teaser:hover,
  .marcel-teaser:focus-visible {
    box-shadow: 0 6px 20px rgba(0,0,0,0.2);
    outline: 2px solid #F8FF66;
    outline-offset: 2px;
  }

  /* Petite pointe vers la bulle */
  .marcel-teaser::after {
    content: '';
    position: absolute;
    right: -5px;
    bottom: 16px;
    width: 8px;
    height: 8px;
    background: #ffffff;
    border-right: 1px solid #0a0a0a;
    border-bottom: 1px solid #0a0a0a;
    transform: rotate(-45deg);
  }

  .marcel-teaser-close {
    position: absolute;
    top: 0;
    right: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    color: #888;
    border-radius: 0 14px 0 12px;
  }

  .marcel-teaser-close:hover,
  .marcel-teaser-close:focus-visible {
    color: #0a0a0a;
    outline: 2px solid #F8FF66;
    outline-offset: -2px;
  }

  .marcel-teaser-close svg {
    width: 12px;
    height: 12px;
  }

  @media (prefers-reduced-motion: reduce) {
    .marcel-teaser {
      transition: none;
      transform: none;
    }
    .marcel-teaser.is-visible { transform: none; }
  }

  /* Mobile : alignement avec la bulle back-to-top, remonte avec le sticky ATC.
     --_marcel-bubble-bottom est mis à jour dynamiquement par JS (_updateBubbleBottom).
     Valeur par défaut : 2.4rem = même position que le bouton back-to-top du thème. */
  @media (max-width: 767px) {
    .marcel-bubble {
      bottom: var(--_marcel-bubble-bottom, 2.4rem);
      right: 16px;
      transition: transform 0.15s ease, box-shadow 0.15s ease, bottom 0.2s ease;
    }
    /* Panneau en plein écran : masquer la bulle quand le chat est ouvert
       (sinon elle flotte par-dessus la conversation). Sur desktop, pas de
       masquage → la bulle reste sous la fenêtre de chat. */
    .marcel-bubble.is-hidden {
      display: none;
    }
    /* Teaser desktop-only (V1). Le format mobile prévu est une « pilule »
       (la bulle s'élargit sur un mot puis se recontracte), pas ce bandeau. */
    .marcel-teaser {
      display: none;
    }
  }

  /* Panneau */
  .marcel-panel {
    position: fixed;
    bottom: 88px;
    right: 20px;
    /* 460px (et non 380px) : les cartes quick-view produit (vignette 96px +
       titre + prix + sélecteur de taille + bouton panier) étouffaient. */
    width: 460px;
    /* Hauteur adaptée à la fenêtre (bouton Marcel 88px en bas + 24px de marge
       en haut), plafonnée à 720px (820px jugé trop haut le 29/09). Avant : 600px fixes — la croix sortait de
       l'écran sous 700px de haut, et le début d'une réponse longue (réponse +
       formulaire) passait hors de vue (retour Pierre 29/09/2026). */
    height: 600px;
    height: min(720px, calc(100vh - 112px));
    height: min(720px, calc(100dvh - 112px));
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 8px 40px rgba(0,0,0,0.18);
    z-index: 9997;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transform: translateY(16px) scale(0.97);
    opacity: 0;
    transition: transform 0.2s ease, opacity 0.2s ease;
    pointer-events: none;
  }

  .marcel-panel.is-open {
    transform: translateY(0) scale(1);
    opacity: 1;
    pointer-events: auto;
  }

  @media (max-width: 767px) {
    .marcel-panel {
      bottom: 0;
      right: 0;
      width: 100%;
      height: 100vh;
      height: 100dvh;
      border-radius: 0;
      overflow: hidden;
    }
  }

  /* Header */
  .marcel-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    background: #0a0a0a;
    color: #ffffff;
    flex-shrink: 0;
  }

  .marcel-header-logo {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  .marcel-header-logo svg {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
  }

  .marcel-header-title {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .marcel-header-subtitle {
    font-size: 11px;
    opacity: 0.6;
    white-space: nowrap;
  }

  .marcel-close-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    color: #ffffff;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    flex-shrink: 0;
    transition: background 0.1s;
  }

  .marcel-close-btn:hover,
  .marcel-close-btn:focus-visible {
    background: rgba(255,255,255,0.15);
    outline: 2px solid rgba(255,255,255,0.5);
    outline-offset: 1px;
  }

  .marcel-close-btn svg {
    width: 18px;
    height: 18px;
  }

  /* Zone dégradée */
  .marcel-degraded {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 24px;
    text-align: center;
    color: #444;
    font-size: 14px;
    line-height: 1.5;
  }

  .marcel-degraded a {
    color: #0a0a0a;
    font-weight: 600;
  }

  /* Body messages */
  /* Bandeau intérim « anglais bientôt » (locales non-FR, avant backend EN). */
  .marcel-en-banner {
    flex: 0 0 auto;
    background: #F8FF66;
    color: #0a0a0a;
    font-size: 12px;
    line-height: 1.4;
    font-weight: 600;
    text-align: center;
    padding: 8px 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .marcel-messages {
    flex: 1;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    scroll-behavior: smooth;
  }

  .marcel-messages::-webkit-scrollbar {
    width: 4px;
  }

  .marcel-messages::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 2px;
  }

  /* Message d'accueil */
  .marcel-welcome {
    background: #f5f5f5;
    border-radius: 12px 12px 12px 4px;
    padding: 12px 14px;
    font-size: 13.5px;
    line-height: 1.55;
    color: #222;
  }

  /* Suggestions */
  .marcel-suggestions {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    margin-top: 4px;
  }

  /* Réponses cliquables sous une question fermée de Marcel (« le motif, ou
     pour la pratique ? »). Même bouton que les chips d'accueil ; marge dédiée
     pour les rattacher visuellement à la bulle qui pose la question. */
  .marcel-quick-replies {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    margin: 8px 0 2px;
  }

  .marcel-suggestion-btn {
    background: #f0f0f0;
    border: 1px solid #e0e0e0;
    border-radius: 20px;
    padding: 10px 14px;
    min-height: 44px;
    font-size: 12px;
    font-weight: 500;
    color: #222;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.1s, border-color 0.1s;
    font-family: inherit;
  }

  .marcel-suggestion-btn:hover,
  .marcel-suggestion-btn:focus-visible {
    background: #0a0a0a;
    border-color: #0a0a0a;
    color: #ffffff;
    outline: 2px solid #F8FF66;
    outline-offset: 1px;
  }

  /* Bulles messages */
  .marcel-msg {
    max-width: 88%;
    font-size: 13.5px;
    line-height: 1.55;
    word-break: break-word;
  }

  .marcel-msg--user {
    align-self: flex-end;
    background: #0a0a0a;
    color: #ffffff;
    border-radius: 16px 16px 4px 16px;
    padding: 10px 14px;
  }

  .marcel-msg--bot {
    align-self: flex-start;
    background: #f5f5f5;
    color: #222;
    border-radius: 16px 16px 16px 4px;
    padding: 10px 14px;
  }

  .marcel-msg--bot a {
    color: #0a0a0a;
    font-weight: 600;
    text-underline-offset: 2px;
  }

  .marcel-msg--bot a:hover {
    text-decoration: none;
  }

  .marcel-md-list {
    margin: 6px 0;
    padding: 0;
    list-style: none;
  }

  .marcel-md-list li {
    position: relative;
    padding-left: 1.1em;
    margin: 2px 0;
  }

  .marcel-md-list li::before {
    content: "•";
    position: absolute;
    left: 0.15em;
    top: 0;
  }

  .marcel-msg--error {
    align-self: flex-start;
    background: #fff0f0;
    color: #c0392b;
    border-radius: 16px 16px 16px 4px;
    padding: 10px 14px;
    font-size: 13px;
  }

  .marcel-msg--error a {
    color: #c0392b;
  }

  /* Source de la réponse */
  .marcel-source {
    font-size: 11.5px;
    color: #777;
    margin-top: 6px;
  }

  .marcel-source a {
    color: #0a0a0a;
    font-weight: 500;
    text-underline-offset: 2px;
  }

  /* Source de type collection : CTA « parcourir » plutôt que citation */
  .marcel-source-cta a {
    font-weight: 600;
  }
  .marcel-source-cta a::after {
    content: " →";
  }

  /* Feedback thumbs — emojis discrets sans pill */
  .marcel-feedback {
    display: flex;
    gap: 2px;
    margin-top: 4px;
    margin-left: -6px;
  }

  .marcel-thumb-btn {
    background: transparent;
    border: none;
    border-radius: 6px;
    padding: 4px 8px;
    min-height: 32px;
    font-size: 12.5px;
    line-height: 1.2;
    cursor: pointer;
    transition: background 0.1s, opacity 0.1s;
    font-family: inherit;
    color: #666;
    opacity: 0.75;
  }

  .marcel-thumb-btn:hover,
  .marcel-thumb-btn:focus-visible {
    opacity: 1;
    background: #eeeeee;
    color: #222;
    outline: 2px solid #F8FF66;
    outline-offset: 1px;
  }

  .marcel-thumb-btn.is-active {
    opacity: 1;
    color: #0a0a0a;
    font-weight: 600;
    background: transparent;
  }

  .marcel-thumb-btn:disabled {
    cursor: default;
  }

  .marcel-thumb-btn.is-hidden {
    display: none;
  }

  /* Toast feedback */
  .marcel-feedback-toast {
    align-self: flex-start;
    font-size: 11.5px;
    color: #555;
    margin-top: 6px;
    padding: 4px 10px;
    background: #f0f0f0;
    border-radius: 10px;
  }

  /* Loading indicator */
  .marcel-loading {
    align-self: flex-start;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 10px 14px;
    background: #f5f5f5;
    border-radius: 16px 16px 16px 4px;
  }

  .marcel-dot {
    width: 7px;
    height: 7px;
    background: #aaa;
    border-radius: 50%;
    animation: marcel-pulse 1.2s ease-in-out infinite;
  }

  .marcel-dot:nth-child(2) { animation-delay: 0.2s; }
  .marcel-dot:nth-child(3) { animation-delay: 0.4s; }

  @keyframes marcel-pulse {
    0%, 80%, 100% { transform: scale(0.7); opacity: 0.5; }
    40% { transform: scale(1); opacity: 1; }
  }

  /* Input zone */
  .marcel-input-area {
    padding: 12px 14px;
    border-top: 1px solid #eeeeee;
    display: flex;
    gap: 8px;
    align-items: center;
    flex-shrink: 0;
    background: #ffffff;
  }

  .marcel-input {
    flex: 1;
    border: 1px solid #e0e0e0;
    border-radius: 24px;
    padding: 9px 14px;
    /* 16px obligatoire : en dessous, iOS Safari zoome automatiquement la page
       au focus du champ (bug constaté mobile 2026-06-20). Ne pas redescendre. */
    font-size: 16px;
    font-family: inherit;
    color: #222;
    background: #fafafa;
    outline: none;
    transition: border-color 0.15s;
    resize: none;
    min-height: 0;
  }

  .marcel-input:focus {
    border-color: #0a0a0a;
    background: #ffffff;
  }

  .marcel-input::placeholder {
    color: #aaa;
  }

  .marcel-send-btn {
    width: 36px;
    height: 36px;
    background: #0a0a0a;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.1s, transform 0.1s;
  }

  .marcel-send-btn:hover,
  .marcel-send-btn:focus-visible {
    background: #333;
    outline: 2px solid #F8FF66;
    outline-offset: 2px;
  }

  .marcel-send-btn:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }

  .marcel-send-btn svg {
    width: 16px;
    height: 16px;
    color: #ffffff;
  }

  /* Accessibilité : neutralise les animations pour les utilisateurs sensibles au mouvement */
  @media (prefers-reduced-motion: reduce) {
    .marcel-panel {
      transition: opacity 0.1s ease;
      transform: translateY(0) scale(1);
    }
    .marcel-bubble {
      transition: none;
    }
    .marcel-dot {
      animation: none;
      opacity: 0.7;
    }
  }

  /* Bouton retour accueil dans le header — pilule libellée « Accueil »
     (visible dès qu'une conversation est en cours, via .is-visible). */
  .marcel-back-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    color: #ffffff;
    padding: 6px 8px;
    min-height: 36px;
    display: none;
    align-items: center;
    justify-content: center;
    gap: 5px;
    border-radius: 6px;
    flex-shrink: 0;
    transition: background 0.1s;
    margin-right: 4px;
    font-family: inherit;
  }

  .marcel-back-btn.is-visible {
    display: inline-flex;
  }

  .marcel-back-btn:hover,
  .marcel-back-btn:focus-visible {
    background: rgba(255,255,255,0.15);
    outline: 2px solid rgba(255,255,255,0.5);
    outline-offset: 1px;
  }

  .marcel-back-btn svg {
    width: 15px;
    height: 15px;
  }

  .marcel-back-label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.01em;
    white-space: nowrap;
  }

  /* Cartes produit (Discovery Commerce) */
  .marcel-products {
    align-self: flex-start;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 88%;
    margin-top: 2px;
  }

  .marcel-card {
    display: flex;
    align-items: stretch;
    gap: 10px;
    background: #ffffff;
    border: 1px solid #ececec;
    border-radius: 12px;
    padding: 8px;
    text-decoration: none;
    color: inherit;
    transition: border-color 0.12s, box-shadow 0.12s, transform 0.12s;
  }

  .marcel-card:hover,
  .marcel-card:focus-visible {
    border-color: #0a0a0a;
    box-shadow: 0 2px 10px rgba(0,0,0,0.08);
    transform: translateY(-1px);
    outline: none;
  }

  .marcel-card:focus-visible {
    outline: 2px solid #F8FF66;
    outline-offset: 2px;
  }

  .marcel-card-img {
    width: 60px;
    height: 60px;
    flex-shrink: 0;
    object-fit: cover;
    border-radius: 8px;
    background: #f5f5f5;
  }

  .marcel-card-body {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 3px;
    min-width: 0;
    flex: 1;
  }

  .marcel-card-title {
    font-size: 13px;
    font-weight: 600;
    color: #0a0a0a;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .marcel-card-vendor {
    font-size: 11px;
    color: #888;
  }

  .marcel-card-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 1px;
  }

  .marcel-card-price {
    font-size: 12.5px;
    font-weight: 600;
    color: #0a0a0a;
  }

  .marcel-card-badge {
    font-size: 10.5px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 10px;
    line-height: 1.4;
    white-space: nowrap;
  }

  .marcel-card-badge--in {
    background: #e7f6ec;
    color: #1b7a3d;
  }

  .marcel-card-badge--restock {
    background: #fff4e0;
    color: #9a5b00;
  }

  .marcel-card-badge--out {
    background: #fdecea;
    color: #c0392b;
  }

  /* Bouton « Reprendre votre dernière conversation » (accueil) */
  .marcel-resume {
    align-self: flex-start;
    display: inline-block;
    margin: 0 0 12px;
    padding: 8px 14px;
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    color: #0a0a0a;
    background: #F8FF66;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    transition: opacity 0.12s;
  }
  .marcel-resume:hover { opacity: 0.85; }
  .marcel-resume:focus-visible {
    outline: 2px solid #0a0a0a;
    outline-offset: 2px;
  }

  /* Lien « voir tous les articles » (fin de liste produit) */
  .marcel-seeall {
    align-self: flex-start;
    display: inline-block;
    margin-top: 2px;
    font-size: 12.5px;
    font-weight: 600;
    color: #0a0a0a;
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
    background: none;
    border: none;
    padding: 2px 0;
  }
  .marcel-seeall:hover { color: #555; }
  .marcel-seeall:focus-visible {
    outline: 2px solid #F8FF66;
    outline-offset: 2px;
    border-radius: 4px;
  }

  /* Puces de collection (motif/longueur) — boutons sous la bulle */
  .marcel-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }
  .marcel-chip {
    display: inline-block;
    padding: 5px 12px;
    font-size: 12.5px;
    font-weight: 600;
    color: #0a0a0a;
    background: #f4f4f4;
    border: 1px solid #e2e2e2;
    border-radius: 999px;
    text-decoration: none;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
  }
  .marcel-chip:hover {
    background: #ececec;
    border-color: #0a0a0a;
  }
  .marcel-chip:focus-visible {
    outline: 2px solid #F8FF66;
    outline-offset: 2px;
  }

  /* Détails dépliés sous la carte produit (desktop : variante + ajout panier +
     lien fiche). S'attache visuellement à la carte (bord commun). */
  .marcel-card.is-expanded {
    border-color: #0a0a0a;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
  .marcel-card-details {
    margin-top: -8px;        /* annule le gap flex pour coller à la carte */
    padding: 10px 12px 12px;
    background: #fafafa;
    border: 1px solid #0a0a0a;
    border-top: none;
    border-radius: 0 0 12px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  /* Quick-view produit (desktop : aperçu in-page sans quitter le chat) */
  .marcel-qv {
    align-self: flex-start;
    display: flex;
    gap: 12px;
    max-width: 88%;
    background: #ffffff;
    border: 1px solid #ececec;
    border-radius: 14px;
    padding: 12px;
    margin-top: 2px;
  }
  .marcel-qv-loading,
  .marcel-qv-error {
    font-size: 13px;
    color: #888;
    padding: 4px 2px;
  }
  .marcel-qv-img {
    width: 96px;
    height: 96px;
    flex-shrink: 0;
    object-fit: cover;
    border-radius: 10px;
    background: #f5f5f5;
  }
  .marcel-qv-body {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    flex: 1;
  }
  .marcel-qv-title {
    font-size: 14px;
    font-weight: 700;
    color: #0a0a0a;
    line-height: 1.25;
  }
  .marcel-qv-price {
    font-size: 13.5px;
    font-weight: 600;
    color: #0a0a0a;
  }
  .marcel-qv-variant {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #555;
  }
  .marcel-qv-select {
    flex: 1;
    min-width: 0;
    font: inherit;
    font-size: 12.5px;
    padding: 5px 6px;
    border: 1px solid #d8d8d8;
    border-radius: 8px;
    background: #fff;
  }
  .marcel-qv-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 2px;
  }
  .marcel-qv-add {
    font: inherit;
    font-size: 12.5px;
    font-weight: 600;
    color: #fff;
    background: #0a0a0a;
    border: none;
    border-radius: 999px;
    padding: 7px 14px;
    cursor: pointer;
    transition: opacity 0.12s;
  }
  .marcel-qv-add:hover { opacity: 0.85; }
  .marcel-qv-add:disabled {
    background: #cfcfcf;
    cursor: not-allowed;
  }
  .marcel-qv-add.is-added { background: #1b7a3d; }
  .marcel-qv-full,
  .marcel-qv-cart {
    font-size: 12.5px;
    font-weight: 500;
    color: #0a0a0a;
    text-underline-offset: 2px;
  }

  /* Lien contact permanent */
  .marcel-contact-footer {
    padding: 8px 14px 12px;
    text-align: center;
    font-size: 12px;
    color: #888;
    flex-shrink: 0;
    border-top: 1px solid #f0f0f0;
  }

  .marcel-contact-footer a {
    color: #0a0a0a;
    font-weight: 500;
    text-underline-offset: 2px;
  }

  /* Mention RGPD + effacement (droit à l'effacement) */
  .marcel-privacy-footer {
    padding: 0 14px 10px;
    text-align: center;
    font-size: 11px;
    line-height: 1.45;
    color: #aaa;
    flex-shrink: 0;
  }
  .marcel-privacy-footer a,
  .marcel-erase-trigger {
    color: #888;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font: inherit;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .marcel-privacy-sep {
    margin: 0 4px;
    color: #ccc;
  }
  /* Acquittement discret après effacement (pas de popup) */
  .marcel-erase-trigger.marcel-erase-done {
    color: #4a7c59;
    text-decoration: none;
    cursor: default;
  }

  /* ── Escalade humaine (formulaire « Laisser un message ») ──────────────── */
  .marcel-escalate-trigger {
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font: inherit;
    font-weight: 500;
    color: #0a0a0a;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .marcel-escalate-cta {
    display: flex;
    justify-content: flex-start;
    padding: 4px 0 2px;
  }
  .marcel-escalate-btn {
    appearance: none;
    border: none;
    cursor: pointer;
    background: #0a0a0a;
    color: #ffffff;
    font: inherit;
    font-weight: 600;
    font-size: 13px;
    padding: 10px 16px;
    border-radius: 12px;
    min-height: 44px;
  }
  .marcel-escalate-btn:hover { background: #2a2a2a; }
  .marcel-degraded-cta { margin-top: 14px; }

  .marcel-escalate-form {
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: #ffffff;
    border: 1px solid #e0e0e0;
    border-radius: 14px;
    padding: 14px;
    margin: 4px 0;
  }
  .marcel-escalate-title { font-weight: 700; font-size: 14px; color: #0a0a0a; }
  .marcel-escalate-intro { font-size: 12px; color: #666; line-height: 1.4; }
  .marcel-escalate-label { font-size: 12px; font-weight: 600; color: #0a0a0a; margin-top: 2px; }
  .marcel-escalate-textarea,
  .marcel-escalate-email {
    width: 100%;
    box-sizing: border-box;
    font: inherit;
    font-size: 16px;
    color: #0a0a0a;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    padding: 9px 11px;
    background: #fafafa;
  }
  .marcel-escalate-textarea { resize: vertical; min-height: 64px; }
  .marcel-escalate-textarea:focus,
  .marcel-escalate-email:focus { outline: none; border-color: #0a0a0a; background: #ffffff; }
  .marcel-escalate-optin {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12px;
    color: #333;
    line-height: 1.4;
    cursor: pointer;
  }
  .marcel-escalate-optin input { margin-top: 2px; flex-shrink: 0; }
  /* Honeypot anti-bot : hors écran, jamais focusable */
  .marcel-escalate-hp {
    position: absolute;
    left: -9999px;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }
  .marcel-escalate-submit {
    appearance: none;
    border: none;
    cursor: pointer;
    background: #0a0a0a;
    color: #ffffff;
    font: inherit;
    font-weight: 600;
    font-size: 14px;
    padding: 11px 16px;
    border-radius: 12px;
    min-height: 44px;
    margin-top: 2px;
  }
  .marcel-escalate-submit:hover { background: #2a2a2a; }
  .marcel-escalate-submit:disabled { opacity: 0.6; cursor: default; }
  .marcel-escalate-feedback { font-size: 12px; color: #666; }
  .marcel-escalate-feedback.is-error { color: #c0392b; }
  .marcel-escalate-feedback.is-error a { color: #c0392b; }
  /* Contrôle e-mail (POST /v1/email-check) — faute de frappe / domaine mort */
  .marcel-escalate-email-msg { font-size: 12px; margin-top: -6px; }
  .marcel-escalate-email-msg:empty { display: none; }
  .marcel-escalate-email-msg.is-suggest { color: #0a0a0a; }
  .marcel-escalate-email-msg.is-suggest .marcel-escalate-email-suggest-btn {
    appearance: none;
    border: none;
    background: none;
    padding: 0;
    margin: 0;
    font: inherit;
    font-weight: 600;
    color: #0a0a0a;
    text-decoration: underline;
    cursor: pointer;
  }
  .marcel-escalate-email-msg.is-error { color: #c0392b; }
  .marcel-escalate-success {
    background: #f1fbe9;
    border: 1px solid #cfe9b0;
    border-radius: 12px;
    padding: 12px 14px;
    font-size: 13px;
    color: #2e5b16;
  }

  /* ── WISMO contextuel (formulaire « Suivre ma commande ») ──────────────── */
  .marcel-wismo-form {
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: #ffffff;
    border: 1px solid #e0e0e0;
    border-radius: 14px;
    padding: 14px;
    margin: 4px 0;
  }
  .marcel-wismo-title { font-weight: 700; font-size: 14px; color: #0a0a0a; }
  .marcel-wismo-intro { font-size: 12px; color: #666; line-height: 1.4; }
  .marcel-wismo-label { font-size: 12px; font-weight: 600; color: #0a0a0a; margin-top: 2px; }
  .marcel-wismo-order,
  .marcel-wismo-email {
    width: 100%;
    box-sizing: border-box;
    font: inherit;
    font-size: 16px;
    color: #0a0a0a;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    padding: 9px 11px;
    background: #fafafa;
  }
  .marcel-wismo-order:focus,
  .marcel-wismo-email:focus { outline: none; border-color: #0a0a0a; background: #ffffff; }
  .marcel-wismo-submit {
    appearance: none;
    border: none;
    cursor: pointer;
    background: #0a0a0a;
    color: #ffffff;
    font: inherit;
    font-weight: 600;
    font-size: 14px;
    padding: 11px 16px;
    border-radius: 12px;
    min-height: 44px;
    margin-top: 2px;
  }
  .marcel-wismo-submit:hover { background: #2a2a2a; }
  .marcel-wismo-submit:disabled { opacity: 0.6; cursor: default; }
  .marcel-wismo-feedback { font-size: 12px; color: #666; }
  .marcel-wismo-feedback.is-error { color: #c0392b; }
  .marcel-wismo-feedback.is-error a { color: #c0392b; }
`;

// ---------------------------------------------------------------------------
// SVG icons (inline, style Lucide)
// ---------------------------------------------------------------------------

const ICON_BUBBLE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`;

const ICON_BACK = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>`;

const ICON_CLOSE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;

const ICON_SEND = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>`;

const ICON_LOGO = `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><rect width="32" height="32" rx="8" fill="#F8FF66"/><path d="M8 22V10h4l4 8 4-8h4v12h-3v-7.5L18 22h-4l-3-7.5V22H8z" fill="#0a0a0a"/></svg>`;

const ICON_UNAVAILABLE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="40" height="40"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`;

// ---------------------------------------------------------------------------
// Textes i18n
// ---------------------------------------------------------------------------

const TEXTS = {
  fr: {
    bubbleLabel: 'Ouvrir Marcel',
    closeLabel: 'Fermer',
    // Invite contextuelle (teaser). Formulée du point de vue du client, comme
    // une question qu'il pourrait poser — au clic elle est pré-remplie dans le
    // champ de saisie. Exception `default` : sans contexte identifiable il n'y
    // a pas de question évidente, c'est donc Marcel qui invite (reprise de la
    // signature du message d'accueil ci-dessous).
    teasers: {
      product: 'Ça taille comment ?',
      cart: 'C’est livré quand ?',
      browse: 'Aidez-moi à choisir',
      account: 'Où en est ma commande ?',
      default: 'Qu’est-ce que je vous sers ?',
    },
    teaserCloseLabel: 'Masquer la suggestion',
    aiDisclaimer: 'Marcel est un assistant automatique, ses réponses peuvent comporter des erreurs.',
    backLabel: 'Accueil',
    headerTitle: 'Marcel',
    headerSubtitle: 'Le Bar à Chaussettes',
    welcome: "Bonjour, je suis Marcel — le barman du Bar à Chaussettes. Livraison, retours, tailles, boutique de Lille… qu'est-ce que je vous sers ?",
    suggestions: [
      'Délais livraison',
      'Comment retourner ?',
      'Guide des tailles',
      'Boutique Lille',
      'Coton bio',
      'Paiements acceptés',
    ],
    placeholder: 'Posez votre question…',
    sendLabel: 'Envoyer',
    badgeInStock: 'En stock',
    badgeRestock: 'En réassort',
    badgeOutStock: 'Rupture',
    seeAll: 'Voir tous les articles',
    resumeLast: 'Reprendre votre dernière conversation',
    quickViewLoading: 'Chargement du produit…',
    quickViewError: 'Impossible de charger ce produit.',
    quickViewAdd: 'Ajouter au panier',
    quickViewAdded: '✓ Ajouté au panier',
    quickViewAddError: 'Réessayer',
    quickViewViewCart: 'Voir le panier',
    quickViewFull: 'Voir la fiche complète',
    quickViewSize: 'Taille',
    quickViewSoldOut: 'Épuisé',
    source: 'Source :',
    feedbackUseful: '👍 Utile',
    feedbackNot: '👎 Insatisfaisant',
    feedbackUsefulLabel: 'Cette réponse est utile',
    feedbackNotLabel: 'Cette réponse est insatisfaisante',
    feedbackThanks: 'Merci pour votre retour !',
    errorFormat: 'La question n\'a pas pu être traitée. Reformulez svp.',
    errorServer: 'Oups, je suis descendu à la cave et j’en ai pour un moment 😊 En attendant, l’équipe prend le relais :',
    errorServerLink: 'contact@lebarachaussettes.com',
    contactLabel: 'Une autre question ?',
    contactLink: 'Laisser un message',
    // Mention RGPD + effacement (droit à l'effacement, DELETE /v1/conversation/{id})
    privacyNotice: 'Vos échanges peuvent être conservés jusqu’à 90 jours pour améliorer notre service.',
    privacyLinkLabel: 'Confidentialité',
    privacyHref: '/policies/privacy-policy',
    eraseLink: 'Effacer cet échange',
    eraseDone: 'Conversation effacée.',
    degradedMsg: 'Marcel est momentanément indisponible. Laissez-nous un message, l’équipe vous répond par email.',
    degradedLink: 'contact@lebarachaussettes.com',
    unavailableTitle: 'Marcel est absent',
    // Escalade humaine → Inbox Ops (POST /v1/escalate)
    escalateBtn: 'Laisser un message à l’équipe',
    escalateTitle: 'Laisser un message',
    escalateIntro: 'L’équipe vous répond par email. Votre échange avec Marcel est joint pour le contexte.',
    escalateMessageLabel: 'Votre message',
    escalateMessagePlaceholder: 'Votre question, le plus précisément possible…',
    escalateEmailLabel: 'Votre email',
    escalateEmailPlaceholder: 'vous@exemple.com',
    escalateOptIn: 'Je souhaite recevoir les offres et nouveautés du Bar à Chaussettes',
    escalateSubmit: 'Envoyer',
    escalateSending: 'Envoi…',
    escalateSuccess: 'Message bien reçu ! L’équipe vous répond par email sous 24-48h ouvrées.',
    escalateError: 'Envoi impossible pour le moment. Réessayez ou écrivez-nous à',
    escalateInvalidEmail: 'Merci d’indiquer un email valide.',
    // Contrôle e-mail (POST /v1/email-check) — faute de frappe / domaine mort
    emailSuggestPrefix: 'Vouliez-vous dire',
    emailNoMailDomain: 'Ce domaine ne semble pas recevoir d’e-mails. Merci de vérifier votre adresse.',
    // WISMO contextuel — chip « Suivre ma commande » → formulaire (POST /v1/order-status)
    wismoFormTitle: 'Suivre ma commande',
    wismoFormIntro: 'Indiquez votre numéro de commande et l’email utilisé à la commande.',
    wismoOrderLabel: 'Numéro de commande',
    wismoOrderPlaceholder: 'F2026123456',
    wismoEmailLabel: 'Votre email',
    wismoEmailPlaceholder: 'vous@exemple.com',
    wismoSubmit: 'Suivre ma commande',
    wismoSending: 'Recherche…',
    wismoInvalidOrder: 'Merci d’indiquer votre numéro de commande.',
    wismoInvalidEmail: 'Merci d’indiquer un email valide.',
    wismoError: 'Recherche impossible pour le moment. Réessayez ou écrivez-nous à',
  },
  en: {
    bubbleLabel: 'Open Marcel',
    closeLabel: 'Close',
    teasers: {
      product: 'How does it fit?',
      cart: 'When will it arrive?',
      browse: 'Help me choose',
      account: 'Where’s my order?',
      default: 'What can I get you?',
    },
    teaserCloseLabel: 'Hide suggestion',
    aiDisclaimer: 'Marcel is an automated assistant; its answers may contain errors.',
    backLabel: 'Home',
    headerTitle: 'Marcel',
    headerSubtitle: 'Le Bar à Chaussettes',
    welcome: "Hi, I'm Marcel — the barman at Le Bar à Chaussettes. Delivery, returns, sizing, our Lille shop… what can I get you?",
    suggestions: [
      'Delivery times',
      'How do I return?',
      'Size guide',
      'Lille shop',
      'Organic cotton',
      'Payment methods',
    ],
    placeholder: 'Ask your question…',
    sendLabel: 'Send',
    badgeInStock: 'In stock',
    badgeRestock: 'Restocking',
    badgeOutStock: 'Out of stock',
    seeAll: 'See all items',
    resumeLast: 'Resume your last conversation',
    quickViewLoading: 'Loading product…',
    quickViewError: 'Could not load this product.',
    quickViewAdd: 'Add to cart',
    quickViewAdded: '✓ Added to cart',
    quickViewAddError: 'Try again',
    quickViewViewCart: 'View cart',
    quickViewFull: 'View full product',
    quickViewSize: 'Size',
    quickViewSoldOut: 'Sold out',
    source: 'Source:',
    feedbackUseful: '👍 Helpful',
    feedbackNot: '👎 Not helpful',
    feedbackUsefulLabel: 'This answer is helpful',
    feedbackNotLabel: 'This answer is not helpful',
    feedbackThanks: 'Thanks for your feedback!',
    errorFormat: 'Your question could not be processed. Please rephrase.',
    errorServer: 'Oops, I’ve popped down to the cellar and I’ll be a while 😊 In the meantime, the team will take over:',
    errorServerLink: 'contact@lebarachaussettes.com',
    contactLabel: 'Another question?',
    contactLink: 'Leave a message',
    // Mention RGPD + effacement (droit à l'effacement, DELETE /v1/conversation/{id})
    privacyNotice: 'Your conversations may be kept for up to 90 days to improve our service.',
    privacyLinkLabel: 'Privacy',
    privacyHref: '/policies/privacy-policy',
    eraseLink: 'Erase this conversation',
    eraseDone: 'Conversation erased.',
    degradedMsg: 'Marcel is momentarily unavailable. Leave us a message and the team will reply by email.',
    degradedLink: 'contact@lebarachaussettes.com',
    unavailableTitle: 'Marcel is away',
    // Escalade humaine → Inbox Ops (POST /v1/escalate)
    escalateBtn: 'Leave a message for the team',
    escalateTitle: 'Leave a message',
    escalateIntro: 'The team will reply by email. Your conversation with Marcel is attached for context.',
    escalateMessageLabel: 'Your message',
    escalateMessagePlaceholder: 'Your question, in as much detail as possible…',
    escalateEmailLabel: 'Your email',
    escalateEmailPlaceholder: 'you@example.com',
    escalateOptIn: 'I’d like to receive offers and news from Le Bar à Chaussettes',
    escalateSubmit: 'Send',
    escalateSending: 'Sending…',
    escalateSuccess: 'Message received! The team will reply by email within 1–2 business days.',
    escalateError: 'Couldn’t send right now. Try again or email us at',
    escalateInvalidEmail: 'Please enter a valid email.',
    // Email check (POST /v1/email-check) — typo / dead domain
    emailSuggestPrefix: 'Did you mean',
    emailNoMailDomain: 'This domain doesn’t seem to accept emails. Please check your address.',
    // WISMO contextuel — chip « Suivre ma commande » → formulaire (POST /v1/order-status)
    wismoFormTitle: 'Track my order',
    wismoFormIntro: 'Enter your order number and the email used for the order.',
    wismoOrderLabel: 'Order number',
    wismoOrderPlaceholder: 'F2026123456',
    wismoEmailLabel: 'Your email',
    wismoEmailPlaceholder: 'you@example.com',
    wismoSubmit: 'Track my order',
    wismoSending: 'Searching…',
    wismoInvalidOrder: 'Please enter your order number.',
    wismoInvalidEmail: 'Please enter a valid email.',
    wismoError: 'Search failed right now. Try again or email us at',
    // Bandeau intérim, affiché uniquement pour une locale NON activée (ex. es/it/de
    // avant leur lot) : l'UI et les réponses restent en français jusqu'à activation.
    bannerSoon: 'Marcel will speak English soon.',
  },
};

// Flag d'activation par langue (Marcel multilingue, Lot 3). Une langue présente
// ici ET dans TEXTS voit son UI traduite + `lang` envoyé au backend. Retirer 'en'
// re-bascule les visiteurs EN sur l'UI FR + bandeau « bientôt » (rollback pur, sans
// re-livraison). Ajouter 'es'/'it'/… à l'activation de leur lot (après seed KB).
const MARCEL_ACTIVE_LANGS = ['fr', 'en'];

// ---------------------------------------------------------------------------
// Web Component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// API publique window.LbcMarcel.openWith — point d'entrée des accroches produit
// (pdp sparks) rendues dans le thème. Le custom element peut ne pas encore être
// connecté quand une accroche appelle openWith (ordre de script indéterminé,
// lazy mount) : on met alors l'appel en file d'attente et on le rejoue à la
// connexion (pattern « queue defer »).
// ---------------------------------------------------------------------------
let _marcelInstance = null;
const _openWithQueue = [];

function _globalOpenWith(opts) {
  if (_marcelInstance) {
    _marcelInstance._openWith(opts || {});
  } else {
    _openWithQueue.push(opts || {});
  }
}

class LbcMarcel extends HTMLElement {
  constructor() {
    super();
    this._shadow = this.attachShadow({ mode: 'open' });
    this._mounted = false;
    this._panelOpen = false;
    this._conversationId = null;
    this._isLoading = false;
    this._degraded = false;
    // Dico i18n courant (Marcel multilingue). Défaut FR ; connectedCallback le
    // remplace par TEXTS[langCode] pour une langue activée (cf. MARCEL_ACTIVE_LANGS).
    this._langCode = 'fr';
    this._texts = TEXTS.fr;
    // Presets d'accueil chargés depuis GET /presets. null = pas (encore) chargé
    // ou échec → on affiche les chips en dur. Tableau (même vide) = source de
    // vérité DB (option A : l'accueil reflète exactement les fiches cochées).
    this._presets = null;
    // Historique de conversation (persisté en localStorage, restaurable).
    this._turns = [];
    // True quand `_turns` porte une conversation antérieure restaurable mais non
    // reprise (accueil / visite avec historique) : la prochaine question saisie
    // démarre alors une conversation NEUVE au lieu de continuer l'ancienne.
    this._pendingNewConversation = false;

    // Lazy mount : au load ou au scroll > 100 px
    this._scrollHandler = this._onScroll.bind(this);
    // Montage différé : handle requestIdleCallback + repli setTimeout.
    this._idleHandle = null;
    this._idleTimer = null;

    // Invite contextuelle (teaser) : armée après le montage, révélée au premier
    // des deux signaux (10 s / scroll 40 %). Une seule apparition par session.
    this._teaserShown = false;
    this._teaserTimer = null;
    this._teaserScrollHandler = this._onTeaserScroll.bind(this);
    this._pageType = '';
    // Origine de session (page d'ouverture) — transmise au backend via page_context.
    this._productId = '';
    this._pagePath = '';
  }

  connectedCallback() {
    // Lire les config depuis :
    // 1. les attributs data- du custom element lui-même (facilite les tests)
    // 2. le script tag data-endpoint[src*="marcel-widget"] (usage Shopify)
    let endpoint = this.dataset.endpoint || '';
    let lang = this.dataset.lang || '';
    let mobileBottom = this.dataset.mobileBottom || '';
    let pageType = this.dataset.pageType || '';
    let productId = this.dataset.productId || '';
    let pagePath = this.dataset.pagePath || '';
    let faceUrl = this.dataset.faceUrl || '';

    if (!endpoint || !lang) {
      const scriptEl = document.querySelector(
        'script[data-endpoint][src*="marcel-widget"]'
      );
      if (scriptEl) {
        endpoint = endpoint || scriptEl.dataset.endpoint || '';
        lang = lang || scriptEl.dataset.lang || '';
        mobileBottom = mobileBottom || scriptEl.dataset.mobileBottom || '';
        pageType = pageType || scriptEl.dataset.pageType || '';
        productId = productId || scriptEl.dataset.productId || '';
        pagePath = pagePath || scriptEl.dataset.pagePath || '';
        faceUrl = faceUrl || scriptEl.dataset.faceUrl || '';
      }
    }

    // `request.page_type` Liquid — route le libellé de l'invite contextuelle.
    // Absent (thème non à jour) → repli sur le libellé `default`.
    this._pageType = pageType;
    // Origine de session : product.id ({{ product.id }}, page fiche uniquement)
    // et chemin de page ({{ request.path }}), transmis au backend (page_context)
    // pour tracer d'où démarre la conversation. Absents (page non-produit, thème
    // non à jour) → simplement non envoyés, aucune régression.
    this._productId = productId;
    this._pagePath = pagePath;
    // Avatar de Marcel (bulle + en-tête). Absent (thème non à jour) → repli
    // sur l'icône chat SVG, aucune régression.
    this._faceUrl = faceUrl;

    this._endpoint = endpoint;
    this._lang = lang || 'fr';
    // Offset bottom de la bulle sur mobile. Valeur par défaut = 2.4rem
    // (même hauteur que le bouton back-to-top du thème Concierge).
    // Configurable via data-mobile-bottom sur le script tag ou le custom element.
    this._mobileBottomBase = mobileBottom || '2.4rem';

    // Résolution de la langue (Marcel multilingue, Lot 3). `ACTIVE_LANGS` est le
    // FLAG D'ACTIVATION par langue : une langue activée utilise son dico `TEXTS`
    // et envoie `lang` au backend (retrieval + réponse dans cette langue) ; retirer
    // une langue de la liste la re-bascule instantanément sur l'UI FR + bandeau
    // « bientôt » (rollback sans re-livraison). Une locale non activée (ex. es/it/de
    // avant leur lot) reste en FR avec le bandeau intérim.
    const langCode = this._lang.split('-')[0].toLowerCase();
    const active = MARCEL_ACTIVE_LANGS.includes(langCode) && !!TEXTS[langCode];
    this._langCode = active ? langCode : 'fr';
    this._texts = TEXTS[this._langCode];
    this._enSoon = !active && langCode !== 'fr';

    // Démarrer lazy mount
    this._setupLazyMount();

    // Enregistrer l'instance pour l'API window.LbcMarcel.openWith et rejouer les
    // appels mis en file avant la connexion (_openWith monte au besoin).
    _marcelInstance = this;
    if (_openWithQueue.length) {
      const pending = _openWithQueue.splice(0);
      pending.forEach((opts) => this._openWith(opts));
    }
  }

  disconnectedCallback() {
    if (_marcelInstance === this) _marcelInstance = null;
    this._cancelPendingMount();
    this._clearTeaserTriggers();
    this._removeFocusTrap();
    if (this._onDocEscape) {
      document.removeEventListener('keydown', this._onDocEscape);
      this._onDocEscape = null;
    }
    this._stickyObserver?.disconnect();
    this._stickyObserver = null;
  }

  // Annule tout montage programmé (idle / timer) et retire le repli scroll.
  _cancelPendingMount() {
    window.removeEventListener('scroll', this._scrollHandler);
    if (this._idleHandle != null) {
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(this._idleHandle);
      }
      this._idleHandle = null;
    }
    if (this._idleTimer != null) {
      clearTimeout(this._idleTimer);
      this._idleTimer = null;
    }
  }

  _setupLazyMount() {
    const tryMount = () => {
      this._cancelPendingMount();
      this._mount();
    };

    // Page déjà chargée (type="module" exécuté après load) → montage direct.
    if (document.readyState === 'complete') {
      this._mount();
      return;
    }

    // Sinon : monter « quand le fil principal est libre », après le premier
    // affichage, SANS attendre window.load (images + scripts tiers) — sinon la
    // bulle traîne sur les pages lourdes. requestIdleCallback cède la priorité
    // au LCP : montage jamais avant le 1er paint (SEO neutre), et la bulle est
    // en position:fixed donc son arrivée tardive ne décale rien (CLS = 0).
    if (typeof window.requestIdleCallback === 'function') {
      this._idleHandle = window.requestIdleCallback(tryMount, { timeout: 2500 });
    } else {
      // Repli (Safari < 16.4, pas de requestIdleCallback) : délai fixe court.
      this._idleTimer = setTimeout(tryMount, 1200);
    }

    // Repli scroll : si l'utilisateur scrolle avant le montage idle.
    window.addEventListener('scroll', this._scrollHandler, { passive: true });
  }

  _onScroll() {
    if (window.scrollY > 100) {
      this._cancelPendingMount();
      this._mount();
    }
  }

  _mount() {
    if (this._mounted) return;
    this._mounted = true;
    this._render();
    this._pingHealth();
    this._updateBubbleBottom();
    // Observer le sticky ATC : quand body[is-product-form-button-fixed] change,
    // la bulle remonte au-dessus de la barre pour rester accessible.
    this._stickyObserver = new MutationObserver(() => this._updateBubbleBottom());
    this._stickyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['is-product-form-button-fixed'],
    });
    // L'invite est armée après le montage : la bulle, elle, reste affichée dès
    // le montage — le délai ne porte que sur le bandeau textuel.
    this._scheduleTeaser();
  }

  // ---------------------------------------------------------------------------
  // Position bulle mobile
  // ---------------------------------------------------------------------------
  _updateBubbleBottom() {
    const isMobile = typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 767px)').matches;
    const base = this._mobileBottomBase || '2.4rem';

    if (isMobile && document.body.hasAttribute('is-product-form-button-fixed')) {
      // Lire la hauteur du sticky ATC depuis les CSS vars du thème
      const stickyH = getComputedStyle(document.documentElement)
        .getPropertyValue('--gsc-product-form-button-wrapper-height').trim() || '8rem';
      this.style.setProperty('--_marcel-bubble-bottom', `calc(${base} + ${stickyH})`);
    } else {
      this.style.setProperty('--_marcel-bubble-bottom', base);
    }
  }

  // ---------------------------------------------------------------------------
  // Invite contextuelle (teaser)
  // ---------------------------------------------------------------------------
  // Clés sessionStorage (tout est borné à la session de navigation : rien ne
  // survit à la fermeture de l'onglet — décision produit, cf. plan §Fréquence).
  _TEASER_OFF_KEY = 'lbc_marcel_teaser_off';    // '1' = plus d'invite cette session
  _TEASER_SEEN_KEY = 'lbc_marcel_teaser_seen';  // CSV des teaser_key déjà montrés
  _TEASER_NAV_KEY = 'lbc_marcel_nav';           // '1' = au moins une page déjà vue
  // Délai à deux vitesses. Sur la 1ʳᵉ page (premier contact), un délai plus long
  // filtre les rebonds. Dès la 2ᵉ page, le visiteur a prouvé qu'il navigue : on
  // affiche vite le message contextuel plutôt que de le faire attendre à nouveau.
  _TEASER_DELAY_FIRST_MS = 6000;
  _TEASER_DELAY_NEXT_MS = 2000;
  _TEASER_SCROLL_RATIO = 0.4;

  /** Ensemble des teaser_key déjà montrés cette session (lecture sessionStorage). */
  _teaserSeenKeys() {
    try {
      const raw = sessionStorage.getItem(this._TEASER_SEEN_KEY);
      return raw ? raw.split(',').filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  }

  /** Libellé d'invite pour le `page_type` courant (repli : `default`). */
  _teaserKeyForPage() {
    switch (this._pageType) {
      case 'product':
        return 'product';
      case 'cart':
        return 'cart';
      case 'collection':
      case 'list-collections':
      case 'search':
        return 'browse';
      case 'customers/account':
      case 'customers/order':
        return 'account';
      default:
        return 'default';
    }
  }

  /**
   * Conditions d'affichage. Toute lecture de storage est protégée : en mode
   * privé / quota plein, l'invite s'affiche plutôt que de casser le widget.
   */
  _teaserAllowed() {
    // Une invite déjà visible à l'écran, ou le panneau ouvert → non.
    if (this._panelVisibleTeaser() || this._panelOpen) return false;
    // Desktop uniquement en V1 (le CSS masque déjà, on évite aussi le timer).
    if (typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 767px)').matches) return false;

    try {
      // Conversation en cours → l'utilisateur connaît déjà Marcel.
      if (localStorage.getItem(this._STORAGE_KEY)) return false;
      // Fermeture (croix) ou chat ouvert plus tôt → stop pour CETTE session.
      // Aucune persistance longue durée : à la prochaine visite on repart à zéro.
      if (sessionStorage.getItem(this._TEASER_OFF_KEY) === '1') return false;

      const seen = this._teaserSeenKeys();
      // Ce contexte a déjà eu son invite cette session → non (une fois par contexte).
      // Pas de plafond global : chaque contexte porte un message DIFFÉRENT et
      // pertinent (5 au total). Le seul frein est le rejet explicite du visiteur
      // (croix ou ouverture du chat) — pas un compteur arbitraire.
      if (seen.includes(this._teaserKeyForPage())) return false;
    } catch (e) { /* storage indispo → on autorise */ }

    return true;
  }

  /** true si un bandeau d'invite est actuellement affiché (is-visible). */
  _panelVisibleTeaser() {
    const t = this._shadow.querySelector('.marcel-teaser');
    return !!t && t.classList.contains('is-visible');
  }

  /** Arme les deux déclencheurs (délai + scroll). Appelé après le montage. */
  _scheduleTeaser() {
    // Marquer la navigation dès le montage : détermine 1ʳᵉ page vs suivantes.
    // Posé avant le check `_teaserAllowed` pour que le compteur reflète les
    // pages vues, pas seulement celles où l'invite était autorisée.
    let firstPage = true;
    try {
      firstPage = !sessionStorage.getItem(this._TEASER_NAV_KEY);
      sessionStorage.setItem(this._TEASER_NAV_KEY, '1');
    } catch (e) { /* storage indispo → traiter comme 1ʳᵉ page */ }

    if (!this._teaserAllowed()) return;
    const delay = firstPage ? this._TEASER_DELAY_FIRST_MS : this._TEASER_DELAY_NEXT_MS;
    this._teaserTimer = setTimeout(() => this._showTeaser(), delay);
    window.addEventListener('scroll', this._teaserScrollHandler, { passive: true });
  }

  _onTeaserScroll() {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;
    if (window.scrollY / scrollable >= this._TEASER_SCROLL_RATIO) this._showTeaser();
  }

  _clearTeaserTriggers() {
    if (this._teaserTimer != null) {
      clearTimeout(this._teaserTimer);
      this._teaserTimer = null;
    }
    window.removeEventListener('scroll', this._teaserScrollHandler);
  }

  _showTeaser() {
    this._clearTeaserTriggers();
    if (!this._teaserAllowed()) return;

    const teaser = this._shadow.querySelector('.marcel-teaser');
    const textEl = this._shadow.querySelector('.marcel-teaser-text');
    if (!teaser || !textEl) return;

    const key = this._teaserKeyForPage();
    const label = (this._texts.teasers || {})[key];
    if (!label) return;

    this._teaserShown = true;
    textEl.textContent = label;
    teaser.hidden = false;
    teaser.removeAttribute('tabindex');
    // Forcer un reflow pour que la transition joue depuis l'état masqué.
    void teaser.offsetWidth;
    teaser.classList.add('is-visible');

    // Mémoriser ce contexte : il ne se remontrera pas cette session (mais un
    // AUTRE contexte, sur une autre page, le pourra — une fois par contexte).
    try {
      const seen = this._teaserSeenKeys();
      if (!seen.includes(key)) {
        seen.push(key);
        sessionStorage.setItem(this._TEASER_SEEN_KEY, seen.join(','));
      }
    } catch (e) { /* ignore */ }
    this._trackEvent('teaser_shown');
  }

  // ---------------------------------------------------------------------------
  // Beacon de mesure — POST /v1/widget-events
  // ---------------------------------------------------------------------------
  _EVENT_SESSION_KEY = 'lbc_marcel_evt_session';

  /**
   * Jeton éphémère de session de navigation, pour relier « invite vue » à
   * « invite cliquée » sans identifier personne. Vit en sessionStorage : il
   * meurt avec l'onglet, n'est jamais envoyé ailleurs, et n'a aucune valeur
   * hors agrégation. Si le storage est indisponible, on renvoie null — les
   * événements restent exploitables en volume.
   */
  _eventSessionId() {
    try {
      let id = sessionStorage.getItem(this._EVENT_SESSION_KEY);
      if (!id) {
        id = generateUUID();
        sessionStorage.setItem(this._EVENT_SESSION_KEY, id);
      }
      return id;
    } catch (e) {
      return null;
    }
  }

  /**
   * Émet un événement de launcher. Fire-and-forget via `fetch(keepalive)` :
   * jamais bloquant, jamais `await`, la mesure ne doit ni peser sur l'UX ni
   * casser le widget si l'endpoint est absent.
   *
   * ⚠️ PAS `navigator.sendBeacon` — mesuré en prod le 20/08/2026 : sur les
   * 1 188 événements reçus depuis juillet, ZÉRO ne venait d'un beacon.
   * `sendBeacon` n'émet jamais de requête préparatoire CORS ; notre endpoint
   * est sur un autre domaine (marcel.lebarachaussettes.com) et n'accepte que
   * `application/json`, un type qui l'exige. Le navigateur abandonne donc la
   * requête — et `sendBeacon()` renvoie quand même `true` : l'échec est
   * invisible côté page comme côté serveur. Le seul type qui passerait sans
   * requête préparatoire (`text/plain`) est refusé par l'API (422, vérifié).
   * `keepalive` couvre le cas que `sendBeacon` servait : survivre à la
   * navigation déclenchée juste après le clic.
   *
   * @param {string} eventType teaser_shown | teaser_click | teaser_close | panel_open
   */
  _trackEvent(eventType) {
    if (!this._endpoint) return;
    const url = this._endpoint.replace(/\/ask$/, '/widget-events');
    const isMobile = typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 767px)').matches;
    const payload = {
      events: [{
        event_type: eventType,
        page_type: this._pageType || null,
        teaser_key: this._teaserKeyForPage(),
        lang: this._langCode,
        device: isMobile ? 'mobile' : 'desktop',
        session_id: this._eventSessionId(),
      }],
    };

    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => { /* mesure best-effort */ });
    } catch (e) { /* jamais bloquant */ }
  }

  /**
   * @param {boolean} stopSession true = plus aucune invite pour CETTE session
   *   de navigation (croix, ou ouverture du chat). Aucune persistance longue
   *   durée : à la prochaine visite (nouvel onglet), l'invite peut réapparaître.
   */
  _hideTeaser(stopSession = false) {
    this._clearTeaserTriggers();
    const teaser = this._shadow.querySelector('.marcel-teaser');
    if (teaser) {
      teaser.classList.remove('is-visible');
      teaser.hidden = true;
      teaser.setAttribute('tabindex', '-1');
    }
    if (stopSession) {
      try {
        sessionStorage.setItem(this._TEASER_OFF_KEY, '1');
      } catch (e) { /* ignore */ }
    }
  }

  // ---------------------------------------------------------------------------
  // Render principal
  // ---------------------------------------------------------------------------
  /**
   * Markup de l'avatar Marcel pour une surface ronde (bulle ou en-tête).
   * Repli sur une icône SVG si aucun `data-face-url` (thème non à jour).
   * @param {string} fallbackSvg  icône affichée si l'avatar est absent
   * @param {string} cssClass     classe de l'`<img>` (bulle vs en-tête)
   */
  _faceMarkup(fallbackSvg, cssClass) {
    if (!this._faceUrl) return fallbackSvg;
    return `<img class="${cssClass}" src="${escapeHtml(this._faceUrl)}" alt="" aria-hidden="true" draggable="false">`;
  }

  _render() {
    const t = this._texts;

    this._shadow.innerHTML = `
      <style>${WIDGET_CSS}</style>

      <!-- Bulle : avatar de Marcel (repli icône chat si data-face-url absent) -->
      <button
        class="marcel-bubble"
        type="button"
        aria-label="${escapeHtml(t.bubbleLabel)}"
        aria-expanded="false"
        aria-controls="marcel-panel"
      >
        ${this._faceMarkup(ICON_BUBBLE, 'marcel-bubble-face')}
      </button>

      <!-- Invite contextuelle : masquée au rendu, révélée par _showTeaser().
           Volontairement sans aria-live : contenu promotionnel non sollicité,
           l'annoncer interromprait la lecture d'écran. La bulle reste le point
           d'entrée accessible. -->
      <button
        class="marcel-teaser"
        type="button"
        hidden
        tabindex="-1"
      >
        <span class="marcel-teaser-text"></span>
        <span
          class="marcel-teaser-close"
          role="button"
          tabindex="0"
          aria-label="${escapeHtml(t.teaserCloseLabel)}"
        >${ICON_CLOSE}</span>
      </button>

      <!-- Panneau -->
      <div
        id="marcel-panel"
        class="marcel-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Marcel — Le Bar à Chaussettes"
      >
        <!-- Header -->
        <div class="marcel-header">
          <button
            class="marcel-back-btn"
            type="button"
            aria-label="Retour à l'écran d'accueil"
            title="Retour à l'accueil"
          >
            ${ICON_BACK}
            <span class="marcel-back-label">${escapeHtml(t.backLabel)}</span>
          </button>
          <div class="marcel-header-logo">
            ${this._faceMarkup(ICON_LOGO, 'marcel-header-face')}
            <div>
              <div class="marcel-header-title">${escapeHtml(t.headerTitle)}</div>
              <div class="marcel-header-subtitle">${escapeHtml(t.headerSubtitle)}</div>
            </div>
          </div>
          <button
            class="marcel-close-btn"
            type="button"
            aria-label="${escapeHtml(t.closeLabel)}"
          >
            ${ICON_CLOSE}
          </button>
        </div>
        ${this._enSoon ? `
        <!-- Bandeau intérim (locale non-FR) : anglais bientôt, UI/réponses FR -->
        <div class="marcel-en-banner" role="status">${escapeHtml(TEXTS.en.bannerSoon)}</div>
        ` : ''}

        <!-- Messages zone (injectée dynamiquement) -->
        <div
          id="marcel-messages"
          class="marcel-messages"
          role="log"
          aria-live="polite"
          aria-label="Conversation Marcel"
        ></div>

        <!-- Input -->
        <div class="marcel-input-area">
          <input
            id="marcel-input"
            class="marcel-input"
            type="text"
            maxlength="500"
            placeholder="${escapeHtml(t.placeholder)}"
            aria-label="${escapeHtml(t.placeholder)}"
            autocomplete="off"
            autocorrect="on"
            autocapitalize="sentences"
            spellcheck="true"
          >
          <button
            id="marcel-send"
            class="marcel-send-btn"
            type="button"
            aria-label="${escapeHtml(t.sendLabel)}"
          >
            ${ICON_SEND}
          </button>
        </div>

        <!-- Footer contact permanent -->
        <div class="marcel-contact-footer">
          ${escapeHtml(t.contactLabel)}
          <button type="button" class="marcel-escalate-trigger">${escapeHtml(t.contactLink)}</button>
        </div>

        <!-- Disclaimer IA + mention RGPD + effacement. Le disclaimer est ici
             (et non sur la bulle) pour encadrer la lecture des réponses au lieu
             de freiner l'entrée dans le chat. -->
        <div class="marcel-privacy-footer">
          ${escapeHtml(t.aiDisclaimer)}
          <br>
          ${escapeHtml(t.privacyNotice)}
          <a href="${escapeHtml(t.privacyHref)}" target="_blank" rel="noopener">${escapeHtml(t.privacyLinkLabel)}</a>
          <span class="marcel-privacy-sep" aria-hidden="true">·</span>
          <button type="button" class="marcel-erase-trigger">${escapeHtml(t.eraseLink)}</button>
        </div>
      </div>
    `;

    this._bindEvents();
    // Réouverture auto après « Voir tous », sinon « Reprendre » sur l'accueil.
    this._initFromStorage();
  }

  // ---------------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------------
  _bindEvents() {
    const shadow = this._shadow;

    const bubble = shadow.querySelector('.marcel-bubble');
    const closeBtn = shadow.querySelector('.marcel-close-btn');
    const backBtn = shadow.querySelector('.marcel-back-btn');
    const sendBtn = shadow.querySelector('#marcel-send');
    const input = shadow.querySelector('#marcel-input');

    // `panel_open` est émis ICI et pas dans _openPanel() : cette méthode est
    // aussi appelée par _openWith() (invite + accroches PDP), qui émettent déjà
    // leur propre événement — on compterait deux fois la même ouverture.
    bubble.addEventListener('click', () => {
      this._trackEvent('panel_open');
      this._openPanel();
    });

    // Invite contextuelle : le clic pré-remplit la question dans le champ de
    // saisie via _openWith() — le MÊME chemin que les accroches PDP (spark).
    // On ne déclenche pas l'appel IA à la place de l'utilisateur : un clic
    // accidentel ne doit ni coûter un appel LLM, ni créer une ligne
    // marcel_sessions (ce qui gonflerait mécaniquement la métrique censée
    // juger l'effet du teaser).
    const teaser = shadow.querySelector('.marcel-teaser');
    const teaserClose = shadow.querySelector('.marcel-teaser-close');

    if (teaser) {
      teaser.addEventListener('click', (e) => {
        // La croix est imbriquée dans le bandeau : ne pas ouvrir sur son clic.
        if (teaserClose && teaserClose.contains(e.target)) return;
        const key = this._teaserKeyForPage();
        const label = (this._texts.teasers || {})[key];
        this._trackEvent('teaser_click');
        // `default` n'est pas une question : ouvrir l'accueil et ses presets.
        // _openWith → _openPanel masque l'invite et pose le stop de session.
        this._openWith(key === 'default' ? {} : { prompt: label });
      });
    }

    if (teaserClose) {
      teaserClose.addEventListener('click', (e) => {
        e.stopPropagation();
        this._trackEvent('teaser_close');
        this._hideTeaser(true);
      });
      // Rôle bouton porté par un <span> (imbrication dans un <button> interdite)
      // → le clavier doit être câblé à la main.
      teaserClose.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          this._trackEvent('teaser_close');
          this._hideTeaser(true);
        }
      });
    }

    closeBtn.addEventListener('click', () => this._closePanel());

    backBtn.addEventListener('click', () => this._resetConversation());

    const eraseTrigger = shadow.querySelector('.marcel-erase-trigger');
    if (eraseTrigger) {
      eraseTrigger.addEventListener('click', () => this._eraseConversation());
    }

    sendBtn.addEventListener('click', () => this._handleSend());

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this._handleSend();
      }
    });

    // Accueil : message de bienvenue + chips de presets. Les chips sont rendus
    // dynamiquement (pilotés par la base de connaissances via GET /presets, avec
    // repli sur les chips en dur si l'appel échoue). Cf. _renderWelcome / _loadPresets.
    const messagesEl = shadow.querySelector('#marcel-messages');
    if (messagesEl) this._renderWelcome(messagesEl);
    this._loadPresets();

    // Déclencheur escalade (footer permanent)
    const escalateTrigger = shadow.querySelector('.marcel-escalate-trigger');
    if (escalateTrigger) {
      escalateTrigger.addEventListener('click', () => this._showEscalateForm());
    }

    // ESC ferme le panneau. L'écouteur shadow ne capte la touche que si le
    // focus est DANS la fenêtre (ex. champ de saisie). On ajoute un écouteur au
    // niveau document pour que Échap ferme aussi quand le focus est ailleurs sur
    // la page. _closePanel() est idempotent → pas de double fermeture.
    shadow.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._panelOpen) {
        this._closePanel();
      }
    });

    this._onDocEscape = (e) => {
      if (e.key === 'Escape' && this._panelOpen) {
        this._closePanel();
      }
    };
    document.addEventListener('keydown', this._onDocEscape);
  }

  // ---------------------------------------------------------------------------
  // Ouverture / fermeture panneau
  // ---------------------------------------------------------------------------
  _openPanel() {
    if (this._panelOpen) return;
    this._panelOpen = true;
    // Le chat est ouvert (par la bulle, l'invite ou une accroche produit) :
    // l'utilisateur a découvert Marcel → plus d'invite pour cette session.
    this._hideTeaser(true);

    const panel = this._shadow.querySelector('.marcel-panel');
    const bubble = this._shadow.querySelector('.marcel-bubble');

    panel.classList.add('is-open');
    bubble.setAttribute('aria-expanded', 'true');
    // Masquer la bulle pendant que le panneau est ouvert : en plein écran mobile
    // elle flottait par-dessus la conversation (bug constaté 2026-06-20).
    bubble.classList.add('is-hidden');
    // Le panneau vit dans le shadow DOM : rien n'est visible de la page.
    // Cet attribut sur l'hôte permet au thème de réagir à l'ouverture
    // (ex. masquer l'onglet Klaviyo « -15 % » qui passait par-dessus le chat
    // en mobile, 30/09/2026).
    this.setAttribute('is-open', '');

    this._lockBodyScroll();

    requestAnimationFrame(() => {
      const input = this._shadow.querySelector('#marcel-input');
      if (!input) panel.focus();
    });

    this._setupFocusTrap();
  }

  _closePanel() {
    if (!this._panelOpen) return;
    this._panelOpen = false;

    const panel = this._shadow.querySelector('.marcel-panel');
    const bubble = this._shadow.querySelector('.marcel-bubble');

    panel.classList.remove('is-open');
    bubble.setAttribute('aria-expanded', 'false');
    // Réafficher la bulle (re-visible avant de lui rendre le focus).
    bubble.classList.remove('is-hidden');
    this.removeAttribute('is-open');

    this._removeFocusTrap();
    this._unlockBodyScroll();

    // Rendre le focus à la bulle
    bubble.focus();
  }

  // ---------------------------------------------------------------------------
  // Ouverture contextuelle depuis une accroche produit (pdp spark)
  // ---------------------------------------------------------------------------
  /**
   * Ouvre le panneau déjà au courant du produit, appelé par
   * window.LbcMarcel.openWith(...) depuis le thème.
   *
   * Static-first : AUCUN appel backend ici. Le message d'accueil est rendu
   * client-side, et la question est pré-remplie (le titre produit y est baké
   * côté Liquid → Marcel ancre sa réponse via ses outils quand l'utilisateur
   * envoie). La copie (greeting/prompt) vient du thème, pas du widget.
   *
   * @param {object} opts
   * @param {string} [opts.intent]      'question' | 'restock' (contexte/attribution)
   * @param {string} [opts.variantId]   GID complet de la variante (attribution)
   * @param {string} [opts.sparkId]     id de l'accroche (attribution)
   * @param {string} [opts.greeting]    message d'accueil proactif (markdown léger)
   * @param {string} [opts.prompt]      question pré-remplie prête à envoyer
   */
  _openWith(opts = {}) {
    const {
      intent = 'question', variantId = null, sparkId = null,
      greeting = '', prompt = '',
    } = opts || {};

    // Contexte spark mémorisé (attribution spark → conversation → vente, §5.8.3).
    this._sparkContext = { intent, variantId, sparkId };

    // Monter si le lazy mount n'a pas encore tourné, puis ouvrir (idempotents).
    if (!this._mounted) this._mount();
    this._openPanel();

    // Message d'accueil proactif — rendu CLIENT-SIDE, zéro appel backend.
    if (greeting) {
      const el = document.createElement('div');
      el.className = 'marcel-msg marcel-msg--bot';
      el.innerHTML = renderMarkdown(greeting);
      this._appendToMessages(el);
    }

    // Pré-remplir l'input avec la question prête à envoyer (contexte produit
    // porté par le texte → pas de modification du backend). L'utilisateur
    // envoie ou édite ; on ne déclenche pas l'appel IA à sa place.
    const input = this._shadow.querySelector('#marcel-input');
    if (input && prompt) input.value = prompt;
    requestAnimationFrame(() => { if (input) input.focus(); });
  }

  _lockBodyScroll() {
    if (typeof window.matchMedia !== 'function') return;
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    this._bodyScrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._bodyScrollY}px`;
    document.body.style.width = '100%';
  }

  _unlockBodyScroll() {
    if (document.body.style.position !== 'fixed') return;
    const scrollY = this._bodyScrollY || 0;
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('width');
    window.scrollTo(0, scrollY);
    this._bodyScrollY = 0;
  }

  // ---------------------------------------------------------------------------
  // Persistance conversation
  //   - localStorage `lbc_marcel_conv` : conversation durable (survit à la
  //     fermeture de l'onglet/navigateur), expirée après _CONV_TTL_MS. Le client
  //     peut « Reprendre votre dernière conversation » depuis l'accueil.
  //   - sessionStorage `lbc_marcel_reopen` : flag transitoire posé juste avant
  //     une navigation site (« Voir tous ») pour rouvrir la bulle + rejouer la
  //     conversation automatiquement à l'arrivée sur la page collection. Ne
  //     déclenche PAS d'ouverture sur une visite ordinaire.
  // Toujours best-effort : un échec (quota, mode privé) est ignoré.
  // ---------------------------------------------------------------------------
  _STORAGE_KEY = 'lbc_marcel_conv';
  _REOPEN_KEY = 'lbc_marcel_reopen';
  _CONV_TTL_MS = 7 * 24 * 60 * 60 * 1000;  // 7 jours

  _persistState() {
    try {
      localStorage.setItem(this._STORAGE_KEY, JSON.stringify({
        conversationId: this._conversationId,
        turns: this._turns,
        savedAt: Date.now(),
      }));
    } catch (e) { /* localStorage indispo / quota → on ignore */ }
  }

  _loadState() {
    try {
      const raw = localStorage.getItem(this._STORAGE_KEY);
      if (!raw) return null;
      const s = JSON.parse(raw);
      if (!s || !Array.isArray(s.turns) || !s.turns.length) return null;
      // Expiration TTL.
      if (typeof s.savedAt === 'number' && (Date.now() - s.savedAt) > this._CONV_TTL_MS) {
        this._clearState();
        return null;
      }
      return s;
    } catch (e) {
      return null;
    }
  }

  _clearState() {
    try { localStorage.removeItem(this._STORAGE_KEY); } catch (e) { /* ignore */ }
  }

  _recordTurn(turn) {
    this._turns.push(turn);
    this._persistState();
  }

  /** Rejoue la conversation persistée dans la zone messages (remplace l'accueil). */
  _replayConversation() {
    const container = this._shadow.querySelector('#marcel-messages');
    if (!container) return;
    container.replaceChildren();
    for (const turn of this._turns) {
      if (turn.role === 'user' && typeof turn.text === 'string') {
        this._appendUserMessage(turn.text);
      } else if (turn.role === 'bot' && turn.data) {
        this._renderBotResponse(turn.data);
      } else if (turn.role === 'wismo' && turn.result) {
        this._appendWismoResult(turn.result);
      }
    }
    this._setBackBtnVisible(true);
    // On est dans la conversation : les prochains envois la continuent.
    this._pendingNewConversation = false;
  }

  /** Bouton « Reprendre votre dernière conversation » en tête de l'accueil. */
  _addResumeButton() {
    const container = this._shadow.querySelector('#marcel-messages');
    if (!container || container.querySelector('.marcel-resume')) return;
    const btn = document.createElement('button');
    btn.className = 'marcel-resume';
    btn.type = 'button';
    btn.textContent = `↩ ${this._texts.resumeLast}`;
    btn.addEventListener('click', () => this._replayConversation());
    container.insertBefore(btn, container.firstChild);
  }

  /** Au montage : restaure l'état persistant (réouverture auto après « Voir tous »,
   *  sinon propose « Reprendre » sur l'accueil). */
  _initFromStorage() {
    const state = this._loadState();
    if (!state) return;

    this._conversationId = state.conversationId || generateUUID();
    this._turns = state.turns;

    let reopen = false;
    try { reopen = sessionStorage.getItem(this._REOPEN_KEY) === '1'; } catch (e) { /* ignore */ }

    if (reopen) {
      // Continuité après une navigation « Voir tous » : rejouer + rouvrir.
      try { sessionStorage.removeItem(this._REOPEN_KEY); } catch (e) { /* ignore */ }
      this._replayConversation();
      this._openPanel();
    } else {
      // Visite ordinaire : ne pas s'ouvrir tout seul, juste proposer de reprendre.
      // Tant que le visiteur ne reprend pas, une nouvelle question démarre une
      // conversation neuve (l'historique chargé reste disponible via Reprendre).
      this._addResumeButton();
      this._pendingNewConversation = true;
    }
  }

  // ---------------------------------------------------------------------------
  // Reset conversation
  // ---------------------------------------------------------------------------
  _resetConversation() {
    const messagesEl = this._shadow.querySelector('#marcel-messages');
    if (!messagesEl) return;

    // Vider la zone messages (replaceChildren est plus propre que innerHTML = '')
    messagesEl.replaceChildren();

    // Réafficher accueil + suggestions
    this._renderWelcome(messagesEl);

    // La conversation n'est PAS détruite : on propose de la reprendre, et tant
    // qu'on ne la reprend pas, taper une question démarre une conversation neuve.
    if (this._turns.length) {
      this._addResumeButton();
      this._pendingNewConversation = true;
    } else {
      this._conversationId = generateUUID();
      this._pendingNewConversation = false;
    }

    // Masquer le bouton retour (on est à nouveau sur l'accueil)
    this._setBackBtnVisible(false);

    // Réactiver l'input sans focus : évite l'ouverture du clavier mobile au retour accueil
    const input = this._shadow.querySelector('#marcel-input');
    if (input) {
      input.disabled = false;
    }
    this._isLoading = false;
  }

  // ---------------------------------------------------------------------------
  // Effacement de la conversation (droit RGPD à l'effacement)
  // ---------------------------------------------------------------------------
  async _eraseConversation() {
    const t = this._texts;

    // Suppression côté serveur (DELETE /v1/conversation/{id}). Best-effort :
    // si l'appel échoue, on efface quand même côté client pour ne pas laisser
    // croire au visiteur que rien n'a été fait. L'endpoint est idempotent.
    if (this._endpoint && this._conversationId) {
      const url = this._endpoint.replace(
        /\/ask$/,
        '/conversation/' + encodeURIComponent(this._conversationId),
      );
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      try {
        await fetch(url, { method: 'DELETE', signal: controller.signal });
      } catch (e) {
        /* non-bloquant */
      }
      clearTimeout(timer);
    }

    // Destruction complète côté client (≠ reset : aucune reprise possible).
    this._turns = [];
    this._clearState();
    this._pendingNewConversation = false;
    this._conversationId = generateUUID();

    const messagesEl = this._shadow.querySelector('#marcel-messages');
    if (messagesEl) {
      messagesEl.replaceChildren();
      this._renderWelcome(messagesEl);
    }
    this._setBackBtnVisible(false);
    const input = this._shadow.querySelector('#marcel-input');
    if (input) {
      input.disabled = false;
    }
    this._isLoading = false;

    // Acquittement discret à l'endroit même de l'action (pas de popup) :
    // le lien « Effacer cet échange » devient « Conversation effacée. »
    // quelques secondes, puis se restaure.
    const eraseBtn = this._shadow.querySelector('.marcel-erase-trigger');
    if (eraseBtn) {
      if (this._eraseAckTimer) clearTimeout(this._eraseAckTimer);
      eraseBtn.textContent = t.eraseDone;
      eraseBtn.disabled = true;
      eraseBtn.classList.add('marcel-erase-done');
      this._eraseAckTimer = setTimeout(() => {
        eraseBtn.textContent = t.eraseLink;
        eraseBtn.disabled = false;
        eraseBtn.classList.remove('marcel-erase-done');
      }, 3500);
    }
  }

  _renderWelcome(container) {
    const t = this._texts;

    const welcomeEl = document.createElement('div');
    welcomeEl.className = 'marcel-welcome';
    welcomeEl.setAttribute('role', 'status');
    welcomeEl.textContent = t.welcome;
    container.appendChild(welcomeEl);

    this._buildSuggestions(container);
  }

  // Liste des chips d'accueil à afficher.
  // Option A (100 % DB-driven) : si les presets ont été chargés (tableau, même
  // vide), on affiche EXACTEMENT les presets cochés en admin. Tant qu'ils ne sont
  // pas chargés OU si le fetch a échoué (this._presets === null), on retombe sur
  // les chips en dur pour ne jamais présenter un accueil vide par accident réseau.
  _suggestionItems() {
    if (Array.isArray(this._presets)) {
      return this._presets.map((p) => ({ id: p.id, label: p.label, action: p.action || null }));
    }
    return this._texts.suggestions.map((s) => ({ id: null, label: s, action: null }));
  }

  // Construit le bloc .marcel-suggestions et l'ajoute à `container`. Chaque chip
  // porte data-suggestion (libellé) et, pour un preset, data-preset-id. Au clic,
  // un preset envoie son preset_id à /v1/ask (réponse curatée sans LLM) ; un chip
  // de repli envoie juste le texte (comportement historique) ; un chip d'ACTION
  // (ex. WISMO, `action: "order_status_form"`, sans preset_id) ouvre un formulaire
  // inline dédié au lieu d'envoyer la question.
  _buildSuggestions(container) {
    const suggestionsEl = document.createElement('div');
    suggestionsEl.className = 'marcel-suggestions';
    suggestionsEl.setAttribute('role', 'group');
    suggestionsEl.setAttribute('aria-label', 'Questions suggérées');

    this._suggestionItems().forEach((item) => {
      const btn = document.createElement('button');
      btn.className = 'marcel-suggestion-btn';
      btn.type = 'button';
      btn.dataset.suggestion = item.label;
      if (item.id != null) btn.dataset.presetId = String(item.id);
      if (item.action) btn.dataset.action = item.action;
      btn.textContent = item.label;
      btn.addEventListener('click', () => {
        if (item.action === 'order_status_form') {
          this._startWismoFlow();
          return;
        }
        const input = this._shadow.querySelector('#marcel-input');
        if (input) input.value = item.label;
        this._handleSend(item.id != null ? item.id : null);
      });
      suggestionsEl.appendChild(btn);
    });

    container.appendChild(suggestionsEl);
  }

  // Charge les presets d'accueil depuis GET /presets et, si on est encore sur
  // l'accueil, remplace les chips de repli par les presets. Best-effort : tout
  // échec (réseau, non-2xx, payload inattendu) laisse this._presets à null →
  // on conserve les chips en dur.
  async _loadPresets() {
    if (!this._endpoint) return;
    // `?lang=` (Marcel multilingue Lot 3.5) : libellés des chips dans la langue
    // du widget (home_label_en côté backend), fallback FR pour une langue non
    // traduite.
    const url = this._endpoint.replace(/\/ask$/, '/presets')
      + '?lang=' + encodeURIComponent(this._langCode);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    try {
      const res = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (!res.ok) return;
      const data = await res.json();
      if (!Array.isArray(data)) return;
      // Un preset classique porte un `id` (marcel_kb.id) ; un chip d'ACTION (WISMO)
      // n'a PAS d'id mais un `action` — les deux sont acceptés ici.
      this._presets = data
        .filter((p) => p && typeof p.label === 'string' && (p.id != null || typeof p.action === 'string'))
        .map((p) => ({
          id: p.id != null ? p.id : null,
          label: p.label,
          action: typeof p.action === 'string' ? p.action : null,
        }));
      this._refreshSuggestions();
    } catch (e) {
      clearTimeout(timer);
      /* repli sur les chips en dur — this._presets reste null */
    }
  }

  // Reconstruit les chips d'accueil après chargement des presets, UNIQUEMENT si
  // l'accueil est toujours affiché (présence d'un bloc .marcel-suggestions ;
  // absent dès qu'une conversation a démarré → on ne touche à rien).
  _refreshSuggestions() {
    const messagesEl = this._shadow && this._shadow.querySelector('#marcel-messages');
    if (!messagesEl) return;
    const existing = messagesEl.querySelector('.marcel-suggestions');
    if (!existing) return;
    existing.remove();
    this._buildSuggestions(messagesEl);
  }

  _setBackBtnVisible(visible) {
    const backBtn = this._shadow.querySelector('.marcel-back-btn');
    if (!backBtn) return;
    if (visible) {
      backBtn.classList.add('is-visible');
    } else {
      backBtn.classList.remove('is-visible');
    }
  }

  // ---------------------------------------------------------------------------
  // Focus trap (A11y)
  // ---------------------------------------------------------------------------
  _setupFocusTrap() {
    this._focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;
      const panel = this._shadow.querySelector('.marcel-panel');
      if (!panel) return;

      const focusable = Array.from(
        panel.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.closest('[style*="display: none"]'));

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = this._shadow.activeElement;

      if (e.shiftKey) {
        if (active === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    this._shadow.addEventListener('keydown', this._focusTrapHandler);
  }

  _removeFocusTrap() {
    if (this._focusTrapHandler) {
      this._shadow.removeEventListener('keydown', this._focusTrapHandler);
      this._focusTrapHandler = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Ping /health
  // ---------------------------------------------------------------------------
  async _pingHealth() {
    if (!this._endpoint) {
      this._setDegraded();
      return;
    }
    const healthUrl = this._endpoint.replace(/\/ask$/, '/health');
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      const res = await fetch(healthUrl, {
        method: 'GET',
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (!res.ok) {
        this._setDegraded();
      }
    } catch {
      this._setDegraded();
    }
  }

  _setDegraded() {
    this._degraded = true;
    const t = this._texts;
    const messagesEl = this._shadow.querySelector('#marcel-messages');
    const inputArea = this._shadow.querySelector('.marcel-input-area');

    if (messagesEl) {
      messagesEl.innerHTML = `
        <div class="marcel-degraded" role="alert">
          ${ICON_UNAVAILABLE}
          <span>${escapeHtml(t.degradedMsg)}</span>
          <button type="button" class="marcel-escalate-btn marcel-degraded-cta">${escapeHtml(t.escalateBtn)}</button>
        </div>
      `;
      const cta = messagesEl.querySelector('.marcel-degraded-cta');
      if (cta) cta.addEventListener('click', () => this._showEscalateForm());
    }
    if (inputArea) {
      inputArea.style.display = 'none';
    }
  }

  // ---------------------------------------------------------------------------
  // Envoi message
  // ---------------------------------------------------------------------------
  _handleSend(presetId = null) {
    if (this._isLoading || this._degraded) return;

    const input = this._shadow.querySelector('#marcel-input');
    if (!input) return;

    const text = input.value.trim();
    if (!text || text.length > 500) return;

    // Conversation antérieure restaurable mais non reprise : taper une question
    // démarre une conversation NEUVE (on n'enchaîne pas sur l'ancienne).
    if (this._pendingNewConversation) {
      this._pendingNewConversation = false;
      this._turns = [];
      this._conversationId = generateUUID();
      const messagesEl = this._shadow.querySelector('#marcel-messages');
      if (messagesEl) messagesEl.replaceChildren();  // retire accueil/suggestions/Reprendre
    }

    // Masquer les suggestions dès le 1er envoi
    const suggestionsEl = this._shadow.querySelector('.marcel-suggestions');
    if (suggestionsEl) suggestionsEl.style.display = 'none';

    // Afficher le bouton retour accueil dès le 1er envoi
    this._setBackBtnVisible(true);

    input.value = '';
    this._appendUserMessage(text);
    this._recordTurn({ role: 'user', text });
    this._askAPI(text, presetId);
  }

  _appendUserMessage(text) {
    const el = document.createElement('div');
    el.className = 'marcel-msg marcel-msg--user';
    el.textContent = text;
    this._appendToMessages(el);
  }

  _appendToMessages(el) {
    const container = this._shadow.querySelector('#marcel-messages');
    if (!container) return;
    container.appendChild(el);
    // Tour = la question du visiteur + la réponse de Marcel qui suit (1er
    // élément hors indicateur de chargement ; sources, cartes et formulaire
    // gardent la même ancre).
    const isUser = el.classList && el.classList.contains('marcel-msg--user');
    const isLoading = el.classList && el.classList.contains('marcel-loading');
    if (isUser) {
      this._turnQuestionEl = el;
      this._turnStartEl = null;
    } else if (!isLoading && !this._turnStartEl) {
      this._turnStartEl = el;
    }
    this._scrollToTurn(
      container,
      isUser || isLoading ? null : this._turnAnchor(container, this._turnQuestionEl, this._turnStartEl),
    );
  }

  /**
   * Ancre du défilement : la QUESTION du visiteur, pour lire la réponse avec
   * ce qui l'a provoquée (retour Pierre 29/09/2026) — sauf si elle occupe plus
   * d'un tiers de la zone visible (long message), auquel cas la réponse.
   */
  _turnAnchor(container, question, answer) {
    if (!answer) return null;
    if (!question || !question.isConnected || !container.contains(question)) return answer;
    const questionHeight = question.getBoundingClientRect().height;
    return questionHeight <= container.clientHeight / 3 ? question : answer;
  }

  /**
   * Fait défiler jusqu'au DÉBUT de la réponse de Marcel si elle dépasse la
   * hauteur visible, sinon jusqu'en bas. Avant : toujours en bas — une réponse
   * suivie d'un formulaire perdait sa première phrase hors de vue.
   */
  _scrollToTurn(container, anchor) {
    if (!anchor || !anchor.isConnected || !container.contains(anchor)) {
      container.scrollTop = container.scrollHeight;
      return;
    }
    const anchorTop = container.scrollTop
      + anchor.getBoundingClientRect().top - container.getBoundingClientRect().top;
    const turnHeight = container.scrollHeight - anchorTop;
    container.scrollTop = turnHeight > container.clientHeight
      ? Math.max(0, anchorTop - 8)
      : container.scrollHeight;
  }

  _showLoading() {
    const el = document.createElement('div');
    el.className = 'marcel-loading';
    el.setAttribute('aria-label', 'Chargement…');
    el.setAttribute('role', 'status');
    el.innerHTML = `
      <div class="marcel-dot"></div>
      <div class="marcel-dot"></div>
      <div class="marcel-dot"></div>
    `;
    el.id = 'marcel-loading-indicator';
    this._appendToMessages(el);
    return el;
  }

  _hideLoading() {
    const el = this._shadow.querySelector('#marcel-loading-indicator');
    if (el) el.remove();
  }

  _setInputEnabled(enabled) {
    const input = this._shadow.querySelector('#marcel-input');
    const sendBtn = this._shadow.querySelector('#marcel-send');
    if (input) input.disabled = !enabled;
    if (sendBtn) sendBtn.disabled = !enabled;
    this._isLoading = !enabled;
  }

  // ---------------------------------------------------------------------------
  // Appel API
  // ---------------------------------------------------------------------------
  /**
   * Contexte de la page d'ouverture (origine de session), transmis au backend.
   * Renvoie null si aucun champ connu. Le backend fige l'origine au 1er tour :
   * l'envoyer à chaque tour est sans effet (ON CONFLICT DO NOTHING côté serveur).
   */
  _buildPageContext() {
    const ctx = {};
    if (this._pageType) ctx.page_type = this._pageType;
    if (this._productId) ctx.product_id = this._productId;
    if (this._pagePath) ctx.path = this._pagePath;
    return Object.keys(ctx).length ? ctx : null;
  }

  async _askAPI(question, presetId = null) {
    if (!this._conversationId) {
      this._conversationId = generateUUID();
    }

    this._setInputEnabled(false);
    const loadingEl = this._showLoading();

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);

    // Sur un clic de preset, on joint preset_id : le backend renvoie la réponse
    // curatée brute (texte + source) sans embed ni LLM. Sans preset_id, flux normal.
    // `lang` (Marcel multilingue, Lot 3) : route le retrieval + la langue de
    // réponse côté backend (défaut 'fr' pour une langue non activée).
    const payload = { question, conversation_id: this._conversationId, lang: this._langCode };
    if (presetId != null) payload.preset_id = presetId;
    const pageContext = this._buildPageContext();
    if (pageContext) payload.page_context = pageContext;

    try {
      const res = await fetch(this._endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timer);
      this._hideLoading();

      if (res.ok) {
        const data = await res.json();
        this._renderBotResponse(data);
        this._recordTurn({ role: 'bot', data });
      } else if (res.status === 422) {
        this._appendErrorMessage(this._texts.errorFormat);
      } else {
        this._appendServerErrorMessage();
      }
    } catch (err) {
      clearTimeout(timer);
      this._hideLoading();
      if (err && err.name === 'AbortError') {
        this._appendServerErrorMessage();
      } else {
        this._appendServerErrorMessage();
      }
    } finally {
      this._setInputEnabled(true);
    }
  }

  // ---------------------------------------------------------------------------
  // Rendu réponse bot
  // ---------------------------------------------------------------------------
  _renderBotResponse(data) {
    const t = this._texts;
    const {
      answer, sources, matched, message_id: messageId, products,
      products_total: productsTotal, products_url: productsUrl,
      collection_chips: collectionChips = [],
      action, quick_replies: quickReplies,
      escalate,
    } = data;

    const wrapper = document.createElement('div');

    const msgEl = document.createElement('div');
    msgEl.className = 'marcel-msg marcel-msg--bot';
    if (messageId) {
      msgEl.setAttribute('data-message-id', messageId);
    }
    msgEl.innerHTML = renderMarkdown(answer || '');
    wrapper.appendChild(msgEl);

    // Réponses cliquables : Marcel vient de poser une question à choix fermé
    // (« Le motif tennis » / « Pour pratiquer »). Le libellé affiché EST la
    // question renvoyée au clic — le backend l'a rédigé pour savoir la router,
    // ce qui évite au visiteur de formuler une réponse que nous devrions
    // deviner. Champ absent → rien de rendu (rétrocompatible).
    const quickRepliesEl = this._renderQuickReplies(quickReplies);
    if (quickRepliesEl) wrapper.appendChild(quickRepliesEl);

    // Cartes produit (Discovery Commerce) — uniquement des produits réels
    // renvoyés par le backend (search_catalog). Aucun rendu si liste absente/vide.
    if (Array.isArray(products) && products.length > 0) {
      const cardsEl = this._renderProductCards(products, productsTotal, productsUrl);
      if (cardsEl) wrapper.appendChild(cardsEl);
    }

    // Puces de collection (motif/longueur) déduites par le backend (answer_links).
    // Boutons cliquables sous la bulle, libellé = mot-clé seul. Handles vérifiés
    // côté serveur → URL relative sûre. Aucune puce → rien de rendu.
    const chipsEl = this._renderCollectionChips(collectionChips);
    if (chipsEl) wrapper.appendChild(chipsEl);

    // Source (si matched et source_url disponible)
    if (matched && sources && sources.length > 0 && sources[0].source_url) {
      const sourceEl = document.createElement('div');
      sourceEl.className = 'marcel-source';
      const sourceUrl = sources[0].source_url;
      const safeUrl = isSafeSourceUrl(sourceUrl) ? escapeHtml(sourceUrl) : null;
      const collLabel = collectionSourceLabel(sourceUrl);
      if (collLabel) {
        // Collection → CTA de navigation (« Parcourez… » / « Voir tous les
        // modèles X »), sans préfixe « Source : » qui se lirait mal.
        sourceEl.classList.add('marcel-source-cta');
        if (safeUrl) {
          sourceEl.innerHTML = `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${escapeHtml(collLabel)}</a>`;
        } else {
          sourceEl.textContent = collLabel;
        }
      } else {
        const sourceText = sources[0].question || sourceUrl;
        if (safeUrl) {
          sourceEl.innerHTML = `${escapeHtml(t.source)} <a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${escapeHtml(sourceText)}</a>`;
        } else {
          sourceEl.textContent = `${t.source} ${sourceText}`;
        }
      }
      wrapper.appendChild(sourceEl);
    }

    // Feedback thumbs (uniquement si matched=true)
    if (matched) {
      const feedbackEl = document.createElement('div');
      feedbackEl.className = 'marcel-feedback';
      feedbackEl.setAttribute('aria-label', 'Cette réponse était-elle utile ?');
      feedbackEl.innerHTML = `
        <button class="marcel-thumb-btn" type="button" data-vote="up" aria-label="${escapeHtml(t.feedbackUsefulLabel)}">${escapeHtml(t.feedbackUseful)}</button>
        <button class="marcel-thumb-btn" type="button" data-vote="down" aria-label="${escapeHtml(t.feedbackNotLabel)}">${escapeHtml(t.feedbackNot)}</button>
      `;
      feedbackEl.querySelectorAll('.marcel-thumb-btn').forEach((btn) => {
        btn.addEventListener('click', () => this._handleFeedback(btn, feedbackEl, msgEl, wrapper));
      });
      wrapper.appendChild(feedbackEl);
    }

    // Plus de bouton "Nouvelle question" sous chaque réponse : le retour à
    // l'accueil se fait via la flèche « Accueil » du bandeau (libellée et
    // visible dès qu'une conversation est en cours).

    this._appendToMessages(wrapper);

    // Question WISMO texte libre détectée côté serveur (court-circuit
    // `wismo_intent`, retour Pierre 2026-07-07) : le formulaire inline
    // s'affiche directement à la suite, comme au clic sur le chip d'accueil.
    if (action === 'order_status_form') {
      this._setBackBtnVisible(true);
      this._showWismoForm();
    }

    // Refus canonique → CTA inline « Laisser un message à l'équipe ».
    // `escalate` est le signal que lbc-ops #1164 a ajouté pour ça : vrai quand
    // la réponse servie est le texte de refus (`refusal_message`) ou quand les
    // chips d'accueil n'ont pas pu être chargées. Ce texte dit au visiteur
    // « laissez-nous un message ci-dessous » — sans ce bouton, il ne désigne
    // rien.
    //
    // Un seul déclencheur, volontairement. On ne se rabat ni sur
    // `matched === false` (les intents `wismo`, `clarify`, `budget_exceeded`,
    // `filtered` le renvoient en servant une réponse utile), ni sur
    // `intent === 'no_match'` : sur ce chemin Sonnet formule lui-même son « je
    // ne sais pas », ne promet aucun bouton, et renvoie le visiteur vers le
    // domaine de Marcel — y proposer d'écrire au SAV encombrerait l'Inbox avec
    // des questions hors sujet (vérifié en production le 2026-09-28).
    if (escalate === true) {
      this._appendEscalateButton();
    }
  }

  // ---------------------------------------------------------------------------
  // Feedback (PATCH /v1/feedback)
  // ---------------------------------------------------------------------------
  async _handleFeedback(clickedBtn, feedbackEl, msgEl, wrapper) {
    // Idempotence côté UI : un seul envoi par message, pas de double-clic
    if (feedbackEl.dataset.feedbackSent === '1') return;
    if (feedbackEl.dataset.feedbackPending === '1') return;

    const messageId = msgEl.getAttribute('data-message-id');
    if (!messageId) return;
    if (!this._endpoint) return;

    const satisfied = clickedBtn.dataset.vote === 'up';
    const feedbackUrl = this._endpoint.replace(/\/ask$/, '/feedback');

    const allBtns = feedbackEl.querySelectorAll('.marcel-thumb-btn');
    feedbackEl.dataset.feedbackPending = '1';
    allBtns.forEach((b) => {
      b.disabled = true;
    });

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);

    try {
      const res = await fetch(feedbackUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message_id: messageId, satisfied }),
        signal: controller.signal,
      });
      clearTimeout(timer);

      if (res.ok) {
        feedbackEl.dataset.feedbackPending = '';
        feedbackEl.dataset.feedbackSent = '1';
        allBtns.forEach((b) => {
          if (b === clickedBtn) {
            b.classList.add('is-active');
          } else {
            b.classList.add('is-hidden');
          }
          // boutons restent disabled — UPDATE idempotent backend mais on évite ré-envoi UI
        });
        this._showFeedbackToast(wrapper, feedbackEl, this._texts.feedbackThanks);
      } else {
        // Erreur silencieuse : retour facultatif, on ne perturbe pas le client.
        // Il peut re-cliquer s'il veut, sinon on oublie.
        this._restoreFeedbackState(feedbackEl, allBtns);
      }
    } catch (_err) {
      clearTimeout(timer);
      this._restoreFeedbackState(feedbackEl, allBtns);
    }
  }

  _restoreFeedbackState(feedbackEl, allBtns) {
    feedbackEl.dataset.feedbackPending = '';
    allBtns.forEach((b) => {
      b.disabled = false;
    });
  }

  _showFeedbackToast(wrapper, feedbackEl, text) {
    // Retirer un éventuel toast précédent
    const existing = wrapper.querySelector('.marcel-feedback-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'marcel-feedback-toast';
    toast.setAttribute('role', 'status');
    toast.textContent = text;
    feedbackEl.insertAdjacentElement('afterend', toast);

    setTimeout(() => {
      if (toast.parentNode) toast.remove();
    }, 2000);
  }

  // ---------------------------------------------------------------------------
  // Rendu puces de collection (motif/longueur cités par Marcel)
  // ---------------------------------------------------------------------------
  // Boutons cliquables sous la bulle. `chips` vient du backend (answer_links) :
  // [{label, url}] avec handles VÉRIFIÉS → URL relative sûre. Le libellé est le
  // mot-clé seul (« Socquettes »). textContent (pas innerHTML) → auto-échappé.
  // Boutons de réponse à une question fermée. Au clic : le libellé part comme
  // question (même chemin qu'une saisie clavier) et le bloc disparaît — les
  // options d'un tour déjà répondu n'ont plus de sens dans le fil.
  _renderQuickReplies(replies) {
    if (!Array.isArray(replies) || replies.length === 0) return null;
    const labels = replies
      .map((r) => (r && typeof r.label === 'string' ? r.label.trim() : ''))
      .filter((label) => label.length > 0);
    if (labels.length === 0) return null;

    const container = document.createElement('div');
    container.className = 'marcel-quick-replies';
    container.setAttribute('role', 'group');
    container.setAttribute('aria-label', 'Réponses proposées');

    labels.forEach((label) => {
      const btn = document.createElement('button');
      btn.className = 'marcel-suggestion-btn';
      btn.type = 'button';
      btn.dataset.quickReply = label;
      btn.textContent = label;
      btn.addEventListener('click', () => {
        container.remove();
        const input = this._shadow.querySelector('#marcel-input');
        if (input) input.value = label;
        this._handleSend();
      });
      container.appendChild(btn);
    });
    return container;
  }

  _renderCollectionChips(chips) {
    if (!Array.isArray(chips) || chips.length === 0) return null;
    const container = document.createElement('div');
    container.className = 'marcel-chips';
    container.setAttribute('role', 'list');
    container.setAttribute('aria-label', 'Collections suggérées');

    let count = 0;
    chips.forEach((c) => {
      if (!c || !c.label || !isSafeProductUrl(c.url)) return;
      const chip = document.createElement('a');
      chip.className = 'marcel-chip';
      chip.setAttribute('role', 'listitem');
      chip.href = c.url;
      // URL relative (/collections/...) → même onglet ; externe → nouvel onglet.
      if (!String(c.url).startsWith('/')) {
        chip.target = '_blank';
        chip.rel = 'noopener noreferrer';
      }
      chip.textContent = c.label;
      container.appendChild(chip);
      count += 1;
    });
    return count > 0 ? container : null;
  }

  // ---------------------------------------------------------------------------
  // Rendu cartes produit (Discovery Commerce)
  // ---------------------------------------------------------------------------
  _renderProductCards(products, total, url) {
    const t = this._texts;
    const container = document.createElement('div');
    container.className = 'marcel-products';
    container.setAttribute('role', 'list');
    container.setAttribute('aria-label', 'Produits suggérés');

    // Au plus MAX_PRODUCT_CARDS cartes ; le reste se consulte via « voir tous ».
    const shown = Array.isArray(products) ? products.slice(0, MAX_PRODUCT_CARDS) : [];

    shown.forEach((p) => {
      if (!p || !p.title) return;

      const safeUrl = isSafeProductUrl(p.url) ? p.url : null;
      const card = document.createElement(safeUrl ? 'a' : 'div');
      card.className = 'marcel-card';
      card.setAttribute('role', 'listitem');
      if (safeUrl) {
        card.href = safeUrl;
        // URL produit relative (/...) ou https : ouvrir dans un nouvel onglet si externe
        if (!safeUrl.startsWith('/')) {
          card.target = '_blank';
          card.rel = 'noopener noreferrer';
        }
        // Clic intercepté : desktop = quick-view in-page (chat ouvert),
        // mobile = fermeture du chat + navigation même onglet. Le href reste
        // pour l'accessibilité et le repli cmd/ctrl-clic (nouvel onglet natif).
        card.addEventListener('click', (e) => this._handleProductClick(e, p, card));
      }

      // Image (https uniquement ; absente → pas de vignette, body pleine largeur)
      const imgHtml = isSafeImageUrl(p.image_url)
        ? `<img class="marcel-card-img" src="${escapeHtml(p.image_url)}" alt="${escapeHtml(p.title)}" loading="lazy" decoding="async">`
        : '';

      const vendorHtml = p.vendor
        ? `<div class="marcel-card-vendor">${escapeHtml(p.vendor)}</div>`
        : '';
      const priceHtml = p.price
        ? `<span class="marcel-card-price">${escapeHtml(p.price)}</span>`
        : '';
      // Badge stock à 3 états : in_stock / restocking / out_of_stock.
      // stock_status piloté par le backend ; fallback sur p.available pour
      // compatibilité avec une réponse sans le champ.
      const stockStatus = p.stock_status || (p.available ? 'in_stock' : 'out_of_stock');
      let badgeClass = 'marcel-card-badge--out';
      let badgeText = t.badgeOutStock;
      if (stockStatus === 'in_stock') {
        badgeClass = 'marcel-card-badge--in';
        badgeText = t.badgeInStock;
      } else if (stockStatus === 'restocking') {
        badgeClass = 'marcel-card-badge--restock';
        badgeText = t.badgeRestock;
      }
      const badgeHtml = `<span class="marcel-card-badge ${badgeClass}">${escapeHtml(badgeText)}</span>`;

      card.innerHTML = `
        ${imgHtml}
        <div class="marcel-card-body">
          <div class="marcel-card-title">${escapeHtml(p.title)}</div>
          ${vendorHtml}
          <div class="marcel-card-meta">
            ${priceHtml}
            ${badgeHtml}
          </div>
        </div>
      `;
      container.appendChild(card);
    });

    if (container.children.length === 0) return null;

    // Lien « voir tous » : libellé fixe sans nombre (on évite d'afficher un
    // comptage de collection approximatif). Affiché uniquement quand la liste
    // dans le chat ne montre PAS tout le catalogue correspondant, c.-à-d. quand
    // le total réel (products_total) dépasse le nombre de cartes affichées.
    // Sans total exploitable (backend ancien), on se rabat sur la troncature de
    // la liste reçue (products.length > cartes affichées).
    const moreUrl = isSafeProductUrl(url) ? url : null;
    const renderedCount = container.children.length;
    const totalNum = Number(total);
    const hasMore = Number.isFinite(totalNum)
      ? totalNum > renderedCount
      : (Array.isArray(products) && products.length > renderedCount);
    if (moreUrl && hasMore) {
      const seeAll = document.createElement('a');
      seeAll.className = 'marcel-seeall';
      seeAll.href = moreUrl;
      seeAll.setAttribute('role', 'listitem');
      seeAll.textContent = `${t.seeAll} →`;
      seeAll.addEventListener('click', (e) => this._navKeepChat(e, moreUrl));
      container.appendChild(seeAll);
    }

    return container;
  }

  // ---------------------------------------------------------------------------
  // Interactions produit (clic carte, quick-view desktop, voir tous)
  // ---------------------------------------------------------------------------

  /** Vrai si l'on est sur la mise en page mobile plein écran (≤ 767px). */
  _isMobile() {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 767px)').matches;
  }

  /** Extrait le handle produit depuis le champ handle ou l'URL /products/<handle>. */
  _productHandle(product) {
    if (product && typeof product.handle === 'string' && product.handle) {
      return product.handle;
    }
    const url = product && product.url;
    if (typeof url === 'string') {
      const m = url.match(/\/products\/([^/?#]+)/);
      if (m) return m[1];
    }
    return null;
  }

  _handleProductClick(e, product, card) {
    // Ouvertures volontaires en nouvel onglet (cmd/ctrl/shift-clic) → comportement natif.
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();

    // Mobile et desktop : déplier/replier les détails AU NIVEAU de la carte (toggle).
    this._toggleCardDetails(card, product);
  }

  /** Déplie un panneau de détails sous la carte (sélecteur variante +
   *  ajout panier + lien fiche). Re-clic sur la carte → replie. */
  async _toggleCardDetails(card, product) {
    if (!card) return;
    const t = this._texts;

    // Re-clic sur la même carte : refermer.
    const sib = card.nextElementSibling;
    if (sib && sib.classList && sib.classList.contains('marcel-card-details')) {
      sib.remove();
      card.classList.remove('is-expanded');
      return;
    }

    // Fermer tout autre panneau ouvert avant d'en ouvrir un nouveau (un seul à la fois).
    this._shadow.querySelectorAll('.marcel-card-details').forEach((d) => {
      const prev = d.previousElementSibling;
      if (prev) prev.classList.remove('is-expanded');
      d.remove();
    });

    const details = document.createElement('div');
    details.className = 'marcel-card-details';
    details.setAttribute('role', 'group');
    // Les clics dans les détails (select, bouton, lien) ne togglent pas la carte.
    details.addEventListener('click', (ev) => ev.stopPropagation());
    details.innerHTML = `<div class="marcel-qv-loading">${escapeHtml(t.quickViewLoading)}</div>`;
    card.insertAdjacentElement('afterend', details);
    card.classList.add('is-expanded');
    // Scroll minimal pour rendre le panneau visible (block: nearest = ne scrolle
    // que si l'élément est hors de la zone visible, sans aller tout en bas).
    details.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    const fallbackFull = () => {
      const fullUrl = isSafeProductUrl(product && product.url) ? product.url : null;
      const link = fullUrl
        ? ` <a class="marcel-qv-full" href="${escapeHtml(fullUrl)}">${escapeHtml(t.quickViewFull)} →</a>`
        : '';
      details.innerHTML = `<div class="marcel-qv-error">${escapeHtml(t.quickViewError)}${link}</div>`;
      const full = details.querySelector('.marcel-qv-full');
      if (full) full.addEventListener('click', (ev) => this._navKeepChat(ev, full.getAttribute('href')));
    };

    const handle = this._productHandle(product);
    if (!handle) { fallbackFull(); return; }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (!res.ok) throw new Error('http');
      const data = await res.json();
      if (!details.isConnected) return;  // refermé entre-temps
      this._fillCardDetails(details, data, product);
    } catch (err) {
      clearTimeout(timer);
      if (details.isConnected) fallbackFull();
    }
  }

  /** Remplit le panneau de détails : sélecteur de variante (si plusieurs) +
   *  bouton « Ajouter au panier » + lien « Voir la fiche complète ». L'image,
   *  le titre et le prix restent portés par la carte au-dessus. */
  _fillCardDetails(details, data, fallback) {
    const t = this._texts;
    const variants = Array.isArray(data && data.variants) ? data.variants : [];

    const fullUrl = isSafeProductUrl(data && data.url)
      ? data.url
      : (fallback && isSafeProductUrl(fallback.url) ? fallback.url : null);

    const firstAvail = variants.find((v) => v && v.available) || variants[0] || null;
    const initialVariantId = firstAvail ? firstAvail.id : null;
    const canBuy = variants.some((v) => v && v.available);

    let variantHtml = '';
    if (variants.length > 1) {
      const opts = variants.map((v) => {
        const label = v.public_title || v.title || '';
        const suffix = v.available ? '' : ` — ${t.quickViewSoldOut}`;
        return `<option value="${escapeHtml(String(v.id))}"${v.available ? '' : ' disabled'}>${escapeHtml(label)}${escapeHtml(suffix)}</option>`;
      }).join('');
      variantHtml = `<label class="marcel-qv-variant"><span>${escapeHtml(t.quickViewSize)}</span><select class="marcel-qv-select" aria-label="${escapeHtml(t.quickViewSize)}">${opts}</select></label>`;
    }

    details.innerHTML = `
      ${variantHtml}
      <div class="marcel-qv-actions">
        <button class="marcel-qv-add" type="button"${canBuy ? '' : ' disabled'}>${escapeHtml(canBuy ? t.quickViewAdd : t.quickViewSoldOut)}</button>
        ${fullUrl ? `<a class="marcel-qv-full" href="${escapeHtml(fullUrl)}">${escapeHtml(t.quickViewFull)} →</a>` : ''}
      </div>
    `;

    const select = details.querySelector('.marcel-qv-select');
    const addBtn = details.querySelector('.marcel-qv-add');
    let variantId = initialVariantId;

    if (select && initialVariantId != null) {
      select.value = String(initialVariantId);
      select.addEventListener('change', () => {
        variantId = select.value;
        const opt = select.options[select.selectedIndex];
        const disabled = !!(opt && opt.disabled);
        if (addBtn) {
          addBtn.disabled = disabled;
          addBtn.textContent = disabled ? t.quickViewSoldOut : t.quickViewAdd;
          // Réinitialiser l'état visuel "Ajouté" quand on change de variante.
          addBtn.classList.remove('is-added');
          addBtn.dataset.pending = '';
        }
      });
    }
    if (addBtn && canBuy) {
      addBtn.addEventListener('click', () => this._addToCart(variantId, addBtn, details));
    }
    // « Voir la fiche complète » : navigation site même onglet en gardant le chat.
    const full = details.querySelector('.marcel-qv-full');
    if (full) full.addEventListener('click', (ev) => this._navKeepChat(ev, full.getAttribute('href')));

    // Recale le scroll après changement de hauteur du contenu (block: nearest =
    // minimal, évite de tout envoyer en bas si la carte est déjà visible).
    details.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  async _addToCart(variantId, btnEl, qvEl) {
    const t = this._texts;
    if (!variantId || !btnEl) return;
    if (btnEl.dataset.pending === '1') return;
    btnEl.dataset.pending = '1';
    btnEl.disabled = true;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        signal: controller.signal,
      });
      clearTimeout(timer);
      btnEl.dataset.pending = '';
      if (!res.ok) throw new Error('cart');

      // Best-effort : notifier le thème pour rafraîchir le tiroir panier (selon thème).
      try { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (e) { /* noop */ }

      btnEl.textContent = t.quickViewAdded;
      btnEl.classList.add('is-added');
      btnEl.disabled = true;

      const actions = qvEl && qvEl.querySelector('.marcel-qv-actions');
      if (actions && !actions.querySelector('.marcel-qv-cart')) {
        const cart = document.createElement('a');
        cart.className = 'marcel-qv-cart';
        cart.href = '/cart';
        cart.textContent = t.quickViewViewCart;
        actions.appendChild(cart);
      }
    } catch (err) {
      clearTimeout(timer);
      btnEl.dataset.pending = '';
      btnEl.disabled = false;
      btnEl.textContent = t.quickViewAddError;
    }
  }

  /** Navigation site (même onglet) en gardant le chat : utilisé par « Voir tous »
   *  ET « Voir la fiche complète ». Desktop : persiste la conversation + pose le
   *  flag de réouverture → la bulle se rouvre avec l'historique sur la page
   *  d'arrivée. Mobile : ferme le chat puis navigue. */
  _navKeepChat(e, url) {
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;  // cmd/ctrl-clic → nouvel onglet natif
    e.preventDefault();
    if (!isSafeProductUrl(url)) return;
    if (this._isMobile()) {
      this._closePanel();
      window.location.assign(url);
    } else {
      this._persistState();
      try { sessionStorage.setItem(this._REOPEN_KEY, '1'); } catch (e) { /* ignore */ }
      window.location.assign(url);
    }
  }

  _appendErrorMessage(text) {
    const el = document.createElement('div');
    el.className = 'marcel-msg marcel-msg--error';
    el.textContent = text;
    this._appendToMessages(el);
  }

  _appendServerErrorMessage() {
    const t = this._texts;
    const el = document.createElement('div');
    // Message bénin (bulle Marcel normale), pas d'alerte rouge ni de lien email :
    // on oriente vers le bouton « Laisser un message à l'équipe » juste en dessous.
    el.className = 'marcel-msg marcel-msg--bot';
    el.textContent = t.errorServer;
    this._appendToMessages(el);
    this._appendEscalateButton();
  }

  // ---------------------------------------------------------------------------
  // Escalade humaine → Inbox Ops (POST /v1/escalate)
  // ---------------------------------------------------------------------------

  /** Bouton « Laisser un message à l'équipe » ajouté au fil. */
  _appendEscalateButton() {
    if (this._shadow.querySelector('.marcel-escalate-form')) return;
    // Un CTA déjà présent mais pas encore cliqué est déplacé en bas du fil
    // plutôt que laissé en place : Marcel dit « en bas de cette fenêtre », et
    // sur deux échecs d'affilée le bouton se retrouverait au-dessus, hors de
    // vue. Un seul CTA à la fois, toujours sous la dernière réponse.
    const stale = this._shadow.querySelector('.marcel-escalate-cta');
    if (stale) stale.remove();
    const t = this._texts;
    const wrap = document.createElement('div');
    wrap.className = 'marcel-escalate-cta';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'marcel-escalate-btn';
    btn.textContent = t.escalateBtn;
    btn.addEventListener('click', () => {
      wrap.remove();
      this._showEscalateForm();
    });
    wrap.appendChild(btn);
    this._appendToMessages(wrap);
  }

  /** Injecte le formulaire d'escalade dans le fil (idempotent). */
  _showEscalateForm() {
    const existing = this._shadow.querySelector('.marcel-escalate-form');
    if (existing) {
      return;
    }
    const t = this._texts;
    const form = document.createElement('form');
    form.className = 'marcel-escalate-form';
    form.setAttribute('novalidate', '');
    form.innerHTML = `
      <div class="marcel-escalate-title">${escapeHtml(t.escalateTitle)}</div>
      <div class="marcel-escalate-intro">${escapeHtml(t.escalateIntro)}</div>
      <label class="marcel-escalate-label" for="marcel-escalate-msg">${escapeHtml(t.escalateMessageLabel)}</label>
      <textarea id="marcel-escalate-msg" class="marcel-escalate-textarea" rows="3" maxlength="2000" placeholder="${escapeHtml(t.escalateMessagePlaceholder)}"></textarea>
      <label class="marcel-escalate-label" for="marcel-escalate-email">${escapeHtml(t.escalateEmailLabel)}</label>
      <input id="marcel-escalate-email" class="marcel-escalate-email" type="email" inputmode="email" autocomplete="email" required aria-describedby="marcel-escalate-email-msg" placeholder="${escapeHtml(t.escalateEmailPlaceholder)}">
      <div id="marcel-escalate-email-msg" class="marcel-escalate-email-msg" role="status" aria-live="polite"></div>
      <label class="marcel-escalate-optin">
        <input type="checkbox" class="marcel-escalate-optin-cb">
        <span>${escapeHtml(t.escalateOptIn)}</span>
      </label>
      <input type="text" class="marcel-escalate-hp" tabindex="-1" autocomplete="off" aria-hidden="true">
      <button type="submit" class="marcel-escalate-submit">${escapeHtml(t.escalateSubmit)}</button>
      <div class="marcel-escalate-feedback" role="status" aria-live="polite"></div>
    `;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this._submitEscalateForm(form);
    });
    const emailField = form.querySelector('.marcel-escalate-email');
    if (emailField) {
      emailField.addEventListener('blur', () => {
        this._checkEscalateEmailField(form);
      });
    }
    this._appendToMessages(form);
  }

  // ---------------------------------------------------------------------------
  // Contrôle e-mail (POST /v1/email-check) — décision Pierre 27/09/2026.
  // Deux garde-fous gratuits : faute de frappe dans le domaine (suggestion,
  // jamais bloquante côté serveur) et domaine qui ne reçoit pas d'e-mails
  // (bloquant). Toute erreur réseau/timeout est traitée comme indéterminée :
  // on ne bloque JAMAIS la saisie sur un problème de contrôle lui-même.
  // ---------------------------------------------------------------------------

  /** Vide/neutralise le message sous le champ e-mail. */
  _clearEmailFieldMessage(form) {
    const msgEl = form.querySelector('.marcel-escalate-email-msg');
    if (!msgEl) return;
    msgEl.textContent = '';
    msgEl.className = 'marcel-escalate-email-msg';
  }

  /**
   * Affiche le verdict `email-check` sous le champ. `suggest` pose un bouton
   * qui remplace la saisie par l'adresse corrigée ; `no_mail_domain` /
   * `invalid_format` affichent une erreur. Tout autre statut efface le message.
   */
  _renderEmailCheckVerdict(form, verdict) {
    const t = this._texts;
    const msgEl = form.querySelector('.marcel-escalate-email-msg');
    const emailEl = form.querySelector('.marcel-escalate-email');
    if (!msgEl || !emailEl) return;

    if (verdict && verdict.status === 'suggest' && verdict.suggestion) {
      const suggestion = String(verdict.suggestion);
      msgEl.innerHTML =
        `${escapeHtml(t.emailSuggestPrefix)} ` +
        `<button type="button" class="marcel-escalate-email-suggest-btn">${escapeHtml(suggestion)}</button> ?`;
      msgEl.className = 'marcel-escalate-email-msg is-suggest';
      const btn = msgEl.querySelector('.marcel-escalate-email-suggest-btn');
      if (btn) {
        btn.addEventListener('click', () => {
          emailEl.value = suggestion;
          form._marcelEmailCheckCache = null;
          form._marcelEmailSuggestionPending = null;
          this._clearEmailFieldMessage(form);
          emailEl.focus();
        });
      }
      return;
    }
    if (verdict && verdict.status === 'no_mail_domain') {
      msgEl.textContent = t.emailNoMailDomain;
      msgEl.className = 'marcel-escalate-email-msg is-error';
      return;
    }
    if (verdict && verdict.status === 'invalid_format') {
      msgEl.textContent = t.escalateInvalidEmail;
      msgEl.className = 'marcel-escalate-email-msg is-error';
      return;
    }
    this._clearEmailFieldMessage(form);
  }

  /**
   * Récupère le verdict `email-check` pour `email`, avec un petit cache par
   * formulaire pour éviter un double appel blur + envoi sur la même valeur.
   * Retourne `null` si indéterminé (réseau, timeout, endpoint absent).
   */
  async _getEmailCheckVerdict(form, email) {
    const cache = form._marcelEmailCheckCache;
    if (cache && cache.email === email) return cache.verdict;
    const verdict = await this._emailCheckAPI(email);
    form._marcelEmailCheckCache = { email, verdict };
    return verdict;
  }

  /** Contrôle au blur : affiche le message, ne bloque jamais rien ici. */
  async _checkEscalateEmailField(form) {
    const emailEl = form.querySelector('.marcel-escalate-email');
    const email = ((emailEl && emailEl.value) || '').trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      this._clearEmailFieldMessage(form);
      return;
    }
    const verdict = await this._getEmailCheckVerdict(form, email);
    this._renderEmailCheckVerdict(form, verdict);
  }

  /**
   * POST /v1/email-check. Retourne `{status, suggestion}` ou `null` si le
   * contrôle n'a pas pu aboutir (jamais bloquant dans ce cas).
   */
  async _emailCheckAPI(email) {
    if (!this._endpoint) return null;
    const url = this._endpoint.replace(/\/ask$/, '/email-check');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (!res || !res.ok) return null;
      const data = await res.json();
      if (!data || typeof data.status !== 'string') return null;
      return data;
    } catch {
      clearTimeout(timer);
      return null;
    }
  }

  async _submitEscalateForm(form) {
    const t = this._texts;
    const emailEl = form.querySelector('.marcel-escalate-email');
    const msgEl = form.querySelector('.marcel-escalate-textarea');
    const optinEl = form.querySelector('.marcel-escalate-optin-cb');
    const hpEl = form.querySelector('.marcel-escalate-hp');
    const feedbackEl = form.querySelector('.marcel-escalate-feedback');
    const submitBtn = form.querySelector('.marcel-escalate-submit');

    const email = ((emailEl && emailEl.value) || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      feedbackEl.textContent = t.escalateInvalidEmail;
      feedbackEl.className = 'marcel-escalate-feedback is-error';
      return;
    }

    const hp = ((hpEl && hpEl.value) || '').trim();
    // Honeypot rempli = bot : on simule le succès sans rien envoyer (et sans
    // consommer de contrôle e-mail — inutile sur une soumission de bot).
    if (hp) {
      this._renderEscalateSuccess(form);
      return;
    }

    // Contrôle e-mail — sauf si CE MÊME e-mail vient déjà d'être bloqué une
    // fois sur une suggestion à cet envoi : un 2e clic sans changement part
    // tel quel (la personne maintient son adresse).
    if (form._marcelEmailSuggestionPending === email) {
      form._marcelEmailSuggestionPending = null;
    } else {
      const verdict = await this._getEmailCheckVerdict(form, email);
      if (verdict && verdict.status === 'suggest' && verdict.suggestion) {
        this._renderEmailCheckVerdict(form, verdict);
        form._marcelEmailSuggestionPending = email;
        return; // bloque une fois, met la question en évidence
      }
      if (verdict && (verdict.status === 'no_mail_domain' || verdict.status === 'invalid_format')) {
        this._renderEmailCheckVerdict(form, verdict);
        return; // domaine mort / format invalide : pas de dérogation
      }
      // status "ok" ou verdict indéterminé (réseau/timeout) → on continue.
      this._clearEmailFieldMessage(form);
    }

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = t.escalateSending;
    }
    feedbackEl.textContent = '';
    feedbackEl.className = 'marcel-escalate-feedback';

    const result = await this._escalateAPI({
      email,
      marketing_opt_in: !!(optinEl && optinEl.checked),
      message: ((msgEl && msgEl.value) || '').trim(),
      hp,
    });
    if (result.ok) {
      this._renderEscalateSuccess(form);
    } else {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = t.escalateSubmit;
      }
      if (result.status === 422 && result.detail) {
        // Filet serveur (domaine mort non intercepté côté client, ex. widget
        // pas à jour) : afficher le message renvoyé par /v1/escalate.
        feedbackEl.textContent = result.detail;
        feedbackEl.className = 'marcel-escalate-feedback is-error';
      } else {
        feedbackEl.innerHTML = `${escapeHtml(t.escalateError)} <a href="mailto:${escapeHtml(t.errorServerLink)}">${escapeHtml(t.errorServerLink)}</a>.`;
        feedbackEl.className = 'marcel-escalate-feedback is-error';
      }
    }
  }

  /**
   * POST /v1/escalate. Renvoie `{ok, status, detail}` (succès, code HTTP,
   * message d'erreur serveur si fourni — ex. 422 domaine mort).
   * Le serveur reconstruit le fil depuis le conversation_id — on ne transmet
   * que l'id, pas le transcript.
   */
  async _escalateAPI({ email, marketing_opt_in, message, hp }) {
    if (!this._endpoint) return { ok: false, status: 0, detail: null };
    const escalateUrl = this._endpoint.replace(/\/ask$/, '/escalate');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const res = await fetch(escalateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          marketing_opt_in: !!marketing_opt_in,
          origin_url:
            typeof window !== 'undefined' && window.location ? window.location.href : '',
          conversation_id: this._conversationId,
          message: message || '',
          hp: hp || '',
        }),
        signal: controller.signal,
      });
      clearTimeout(timer);
      const ok = !!(res && res.ok);
      let detail = null;
      if (res && !ok) {
        try {
          const data = await res.json();
          if (data && typeof data.detail === 'string') detail = data.detail;
        } catch {
          detail = null;
        }
      }
      return { ok, status: res ? res.status : 0, detail };
    } catch {
      clearTimeout(timer);
      return { ok: false, status: 0, detail: null };
    }
  }

  _renderEscalateSuccess(form) {
    const t = this._texts;
    const el = document.createElement('div');
    el.className = 'marcel-escalate-success';
    el.setAttribute('role', 'status');
    el.textContent = t.escalateSuccess;
    form.replaceWith(el);
    const container = this._shadow.querySelector('#marcel-messages');
    if (container) container.scrollTop = container.scrollHeight;
  }

  // ---------------------------------------------------------------------------
  // WISMO contextuel — chip « Suivre ma commande » → formulaire → POST /v1/order-status
  // (Marcel V4.5 phase 1a). Faits assemblés côté serveur, LLM porte-parole groundé :
  // le widget ne fait qu'afficher `answer` telle que renvoyée, jamais de logique
  // métier ici.
  // ---------------------------------------------------------------------------

  /**
   * Point d'entrée du chip WISMO : démarre une conversation NEUVE — même
   * bootstrap qu'une question tapée au clavier (_handleSend) — jamais la
   * reprise d'une ancienne conversation restaurable (_pendingNewConversation).
   */
  _startWismoFlow() {
    if (this._pendingNewConversation) {
      this._pendingNewConversation = false;
      this._turns = [];
      this._conversationId = generateUUID();
      const messagesEl = this._shadow.querySelector('#marcel-messages');
      if (messagesEl) messagesEl.replaceChildren();
    }
    const suggestionsRow = this._shadow.querySelector('.marcel-suggestions');
    if (suggestionsRow) suggestionsRow.style.display = 'none';
    this._setBackBtnVisible(true);
    this._showWismoForm();
  }

  /** Injecte le formulaire WISMO dans le fil (idempotent). */
  _showWismoForm() {
    const existing = this._shadow.querySelector('.marcel-wismo-form');
    if (existing) return;
    const t = this._texts;
    const form = document.createElement('form');
    form.className = 'marcel-wismo-form';
    form.setAttribute('novalidate', '');
    form.innerHTML = `
      <div class="marcel-wismo-title">${escapeHtml(t.wismoFormTitle)}</div>
      <div class="marcel-wismo-intro">${escapeHtml(t.wismoFormIntro)}</div>
      <label class="marcel-wismo-label" for="marcel-wismo-order">${escapeHtml(t.wismoOrderLabel)}</label>
      <input id="marcel-wismo-order" class="marcel-wismo-order" type="text" maxlength="32" autocomplete="off" required placeholder="${escapeHtml(t.wismoOrderPlaceholder)}">
      <label class="marcel-wismo-label" for="marcel-wismo-email">${escapeHtml(t.wismoEmailLabel)}</label>
      <input id="marcel-wismo-email" class="marcel-wismo-email" type="email" inputmode="email" autocomplete="email" required placeholder="${escapeHtml(t.wismoEmailPlaceholder)}">
      <button type="submit" class="marcel-wismo-submit">${escapeHtml(t.wismoSubmit)}</button>
      <div class="marcel-wismo-feedback" role="status" aria-live="polite"></div>
    `;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this._submitWismoForm(form);
    });
    this._appendToMessages(form);
  }

  _submitWismoForm(form) {
    const t = this._texts;
    const orderEl = form.querySelector('.marcel-wismo-order');
    const emailEl = form.querySelector('.marcel-wismo-email');
    const feedbackEl = form.querySelector('.marcel-wismo-feedback');
    const submitBtn = form.querySelector('.marcel-wismo-submit');

    const orderNo = ((orderEl && orderEl.value) || '').trim();
    if (!orderNo) {
      feedbackEl.textContent = t.wismoInvalidOrder;
      feedbackEl.className = 'marcel-wismo-feedback is-error';
      return;
    }
    const email = ((emailEl && emailEl.value) || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      feedbackEl.textContent = t.wismoInvalidEmail;
      feedbackEl.className = 'marcel-wismo-feedback is-error';
      return;
    }

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = t.wismoSending;
    }
    feedbackEl.textContent = '';
    feedbackEl.className = 'marcel-wismo-feedback';

    this._orderStatusAPI({ order_no: orderNo, email }).then((result) => {
      if (result) {
        // Enregistré comme n'importe quel tour de conversation (persistance
        // locale « Reprendre » + conversation_id envoyé au serveur, cf.
        // _orderStatusAPI) — pas un échange à part.
        this._recordTurn({ role: 'user', text: t.wismoFormTitle });
        this._recordTurn({ role: 'wismo', result });
        this._renderWismoResult(form, result);
      } else {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = t.wismoSubmit;
        }
        feedbackEl.innerHTML = `${escapeHtml(t.wismoError)} <a href="mailto:${escapeHtml(t.errorServerLink)}">${escapeHtml(t.errorServerLink)}</a>.`;
        feedbackEl.className = 'marcel-wismo-feedback is-error';
      }
    });
  }

  /**
   * POST /v1/order-status. Renvoie le payload {matched, answer, status_code,
   * tracking_url} ou `null` en cas d'échec réseau/serveur (429 rate-limit, 503
   * flag off, timeout). Le serveur gère seul l'anti-pêche : le widget affiche
   * `answer` telle quelle dans tous les cas de réponse 2xx, y compris matched=false.
   * `conversation_id` rattache l'échange à la conversation courante côté serveur
   * (GET /v1/conversations admin) — génère un id si aucune conversation n'a
   * encore démarré (même filet que _askAPI).
   */
  async _orderStatusAPI({ order_no, email }) {
    if (!this._endpoint) return null;
    if (!this._conversationId) {
      this._conversationId = generateUUID();
    }
    const url = this._endpoint.replace(/\/ask$/, '/order-status');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ order_no, email, conversation_id: this._conversationId, lang: this._langCode }),
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (!res.ok) return null;
      return await res.json();
    } catch {
      clearTimeout(timer);
      return null;
    }
  }

  /** Construit le bloc de réponse WISMO (texte + lien de suivi si expédiée) —
   *  partagé entre le rendu live (_renderWismoResult) et le replay (_appendWismoResult). */
  _buildWismoResultEl(result) {
    // Le lien de suivi, s'il existe, est déjà intégré par le serveur dans
    // `answer` en Markdown `[texte](url)` (jamais l'URL brute) — renderMarkdown
    // le rend cliquable. Pas de bloc .marcel-source séparé : il ferait doublon
    // avec le lien inline (retour Pierre 2026-07-02).
    const answer = (result && result.answer) || '';
    const wrapper = document.createElement('div');
    const msgEl = document.createElement('div');
    msgEl.className = 'marcel-msg marcel-msg--bot';
    msgEl.innerHTML = renderMarkdown(answer);
    wrapper.appendChild(msgEl);
    return wrapper;
  }

  /** Ajoute le résultat WISMO en fin de fil (replay d'une conversation persistée). */
  _appendWismoResult(result) {
    this._appendToMessages(this._buildWismoResultEl(result));
  }

  /** Remplace le formulaire par la bulle utilisateur + la réponse groundée. */
  _renderWismoResult(form, result) {
    const userEl = document.createElement('div');
    userEl.className = 'marcel-msg marcel-msg--user';
    userEl.textContent = this._texts.wismoFormTitle;
    const wrapper = document.createElement('div');
    wrapper.appendChild(userEl);
    wrapper.appendChild(this._buildWismoResultEl(result));
    form.replaceWith(wrapper);
    const container = this._shadow.querySelector('#marcel-messages');
    if (container) {
      this._scrollToTurn(
        container,
        this._turnAnchor(container, wrapper.firstElementChild, wrapper.lastElementChild),
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Helper URL whitelist pour sources
// ---------------------------------------------------------------------------
function isSafeSourceUrl(url) {
  return typeof url === 'string' && /^(https?:\/\/|\/)/i.test(url.trim());
}

// ---------------------------------------------------------------------------
// Libellé de source pour les collections
// ---------------------------------------------------------------------------
// Quand une source pointe vers une collection (et non une page / blog / FAQ /
// policy), « Source : <question> » se lit mal : ce n'est pas une citation mais
// une invitation à parcourir. On remplace le libellé par un CTA de navigation :
//   • collection d'une marque distribuée → « Voir tous les modèles <Marque> »
//   • collection promotions / soldes      → « Voir les promotions du moment »
//   • toute autre collection               → « Parcourez tout notre catalogue »
// Le handle d'une collection marque = le nom de la marque « slugifié »
// (dedoles, sock-affairs…), convention vérifiée côté Shopify. Panneau FR-only
// (cf. topologie widget Marcel) → libellés en français.
const BRAND_BY_COLLECTION_HANDLE = {
  'dedoles': 'Dedoles',
  'sock-affairs': 'Sock Affairs',
  'pirin-hill': 'Pirin Hill',
  'besocks': 'Besocks',
  'many-mornings': 'Many Mornings',
  'try-to-fly': 'Try To Fly',
  'billybelt': 'Billybelt',
  'tabio': 'Tabio',
  'atelier-st-eustache': 'Atelier St Eustache',
  'felizz': 'Felizz',
  'takapara': 'Takapara',
};

/**
 * Renvoie un libellé de navigation si `url` pointe vers une collection Shopify,
 * sinon `null` (→ le widget conserve « Source : <question> »).
 */
function collectionSourceLabel(url) {
  if (typeof url !== 'string') return null;
  const m = url.match(/\/collections\/([^/?#]+)/i);
  if (!m) return null;
  let handle;
  try {
    handle = decodeURIComponent(m[1]).toLowerCase();
  } catch (e) {
    handle = m[1].toLowerCase();
  }
  const brand = BRAND_BY_COLLECTION_HANDLE[handle];
  if (brand) return `Voir tous les modèles ${brand}`;
  if (/^(promotions?|soldes|promos?|sale)$/.test(handle)) {
    return 'Voir les promotions du moment';
  }
  return 'Parcourez tout notre catalogue';
}

/** URL produit autorisée pour un lien de carte : https ou chemin relatif. */
function isSafeProductUrl(url) {
  return typeof url === 'string' && /^(https:\/\/|\/)/i.test(url.trim());
}

/** URL image autorisée dans un <img src> : https uniquement (CDN Shopify). */
function isSafeImageUrl(url) {
  return typeof url === 'string' && /^https:\/\//i.test(url.trim());
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------
if (typeof customElements !== 'undefined' && !customElements.get('lbc-marcel')) {
  customElements.define('lbc-marcel', LbcMarcel);
}

// API publique consommée par les accroches produit (pdp sparks) du thème.
// Exposée sur window même si le custom element n'est pas encore connecté :
// _globalOpenWith met l'appel en file et le rejoue à la connexion.
if (typeof window !== 'undefined') {
  window.LbcMarcel = window.LbcMarcel || {};
  window.LbcMarcel.openWith = _globalOpenWith;
}

// Export pour les tests Vitest (ESM)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { LbcMarcel, renderMarkdown, escapeHtml, generateUUID, isSafeProductUrl, isSafeImageUrl, collectionSourceLabel, _globalOpenWith };
}
export { LbcMarcel, renderMarkdown, escapeHtml, generateUUID, isSafeProductUrl, isSafeImageUrl, collectionSourceLabel, _globalOpenWith };
